// src/pages/CourseDesigner.js
import React, { useState, useEffect, useRef, useCallback } from "react";


// ── Constants ─────────────────────────────────────────────────────────────────
export const BLOCK_TYPES = {
  text:       { label:"Text / Rich Text",       icon:"¶",  group:"informational", desc:"Add formatted text, headings, lists and inline styling." },
  image:      { label:"Image",                  icon:"🖼",  group:"informational", desc:"Upload or embed a photo or graphic." },
  video:      { label:"Video",                  icon:"▶",  group:"informational", desc:"Embed a YouTube video or upload your own." },
  info:       { label:"Info / Callout",         icon:"ℹ",  group:"informational", desc:"Highlight a tip, warning or key message in a coloured box." },
  keypoints:  { label:"Key Points",             icon:"✦",  group:"informational", desc:"Summarise the main takeaways as a bullet list." },
  quote:      { label:"Quote / Testimonial",    icon:"❝",  group:"informational", desc:"Display a pull-quote or attributed testimonial." },
  table:      { label:"Table",                  icon:"⊞",  group:"informational", desc:"Present structured data in rows and columns." },
  process:    { label:"Process Steps",          icon:"①",  group:"informational", desc:"Walk through a numbered sequence of steps." },
  flowchart:  { label:"Flowchart",              icon:"⬦",  group:"informational", desc:"Show a branching decision or process diagram." },
  accordion:  { label:"Accordion",              icon:"☰",  group:"informational", desc:"Collapsible sections for FAQs or layered content." },
  tabs:       { label:"Tabs",                   icon:"⊟",  group:"informational", desc:"Organise content into switchable tabbed panels." },
  columns:    { label:"2 / 3 Columns",          icon:"▥",  group:"informational", desc:"Place content side-by-side in two or three columns." },
  divider:    { label:"Divider",                icon:"—",  group:"informational", desc:"Insert a horizontal rule to separate content areas." },
  scenario:   { label:"Scenario / Branching",   icon:"⑂",  group:"interactive",   desc:"Present a situation and let learners choose a path." },
  flashcard:  { label:"Flashcards",             icon:"🃏",  group:"interactive",   desc:"Flip cards to reveal answers — great for terminology." },
  hotspot:    { label:"Hotspot Image",          icon:"🎯",  group:"interactive",   desc:"Clickable zones on an image that reveal information." },
  matching:   { label:"Matching Pairs",         icon:"⇌",  group:"interactive",   desc:"Drag items to match pairs — tests associations." },
  quiz_mc:    { label:"Multiple Choice",        icon:"◉",  group:"questions",     desc:"One correct answer from several options." },
  quiz_ms:    { label:"Multi-Select",           icon:"☑",  group:"questions",     desc:"Select all correct answers from a list." },
  quiz_tf:    { label:"True / False",           icon:"✓✗", group:"questions",     desc:"Learner decides whether a statement is true or false." },
  quiz_dd:    { label:"Drag & Drop",            icon:"⇄",  group:"questions",     desc:"Drag items into the correct order or category." },
};

// Group metadata (used for block badges and existing references)
export const BLOCK_GROUPS = [
  { id: "informational", label: "Informational", color: "#6366F1" },
  { id: "interactive",   label: "Interactive",   color: "#059669" },
  { id: "questions",     label: "Questions",     color: "#DC2626" },
];

const COMPLETION_MODES = [
  { value: "last_page",     label: "User reaches the last page"          },
  { value: "pass_quiz",     label: "User passes the final quiz"          },
  { value: "last_and_pass", label: "Last page AND pass the final quiz"   },
];

const SEC0_ID = "section_intro";
const SEC1_ID = "section_1";

const DEFAULT_INTRO_BLOCK = { id:"block_intro_text", type:"text", html:"<p>Welcome to this course. Use this space to introduce the topic, outline what learners will cover, and set expectations.</p>" };

const DEFAULT_DESIGN = {
  pages: [
    { id:"page_intro", title:"Introduction", blocks:[DEFAULT_INTRO_BLOCK], isFinalQuiz:false, sectionId:SEC0_ID },
  ],
  sections:     [ { id:SEC1_ID, title:"Section 1" } ],
  introEnabled: true,
  theme:        { primaryColor: "#F54927", title: "", subtitle: "" },
  passScore:     80,
  quizBankCount: 5,
};

const INFO_STYLES = {
  info:      { bg:"#EFF6FF", border:"#3B82F6", icon:"ℹ️" },
  tip:       { bg:"#F0FDF4", border:"#22C55E", icon:"💡" },
  warning:   { bg:"#FFFBEB", border:"#F59E0B", icon:"⚠️" },
  important: { bg:"var(--red-light)", border:"var(--red)", icon:"🔴" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function newBlock(type) {
  const id = `${type}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
  const base = { id, type };
  if (type === "quiz_mc")   return { ...base, question:"", options:[{text:"",correct:true},{text:"",correct:false},{text:"",correct:false},{text:"",correct:false}], explanation:"", points:1, inBank:true, alwaysShow:false };
  if (type === "quiz_ms")   return { ...base, question:"", options:[{text:"",correct:false},{text:"",correct:false},{text:"",correct:false},{text:"",correct:false}], requiredCount:2, explanation:"", points:1, inBank:true, alwaysShow:false };
  if (type === "quiz_tf")   return { ...base, question:"", correct:0, explanation:"", points:1, inBank:true, alwaysShow:false };
  if (type === "quiz_dd")   return { ...base, instruction:"Drag each item to the correct category", items:[{text:"",category:0},{text:"",category:1},{text:"",category:0},{text:"",category:1}], categories:["Category A","Category B"], explanation:"", points:1, inBank:true, alwaysShow:false };
  if (type === "keypoints") return { ...base, heading:"", points:[""] };
  if (type === "info")      return { ...base, heading:"", body:"", style:"info" };
  if (type === "quote")     return { ...base, text:"", attribution:"", role:"" };
  if (type === "table")     return { ...base, heading:"", headers:["Column 1","Column 2","Column 3"], rows:[["","",""],["","",""]] };
  if (type === "process")   return { ...base, heading:"", steps:[{title:"Step 1",body:""},{title:"Step 2",body:""}] };
  if (type === "flowchart") return { ...base, steps:[{label:"Start",body:""},{label:"Step 2",body:""},{label:"End",body:""}] };
  if (type === "accordion") return { ...base, heading:"", items:[{title:"Item 1",body:""},{title:"Item 2",body:""}] };
  if (type === "tabs")      return { ...base, tabs:[{label:"Tab 1",body:""},{label:"Tab 2",body:""}] };
  if (type === "columns")   return { ...base, count:2, col1:"", col2:"", col3:"" };
  if (type === "divider")   return { ...base, label:"", style:"line" };
  if (type === "scenario")  return { ...base, situation:"", choices:[{label:"Option A",feedback:"",outcome:"neutral"},{label:"Option B",feedback:"",outcome:"neutral"}] };
  if (type === "flashcard") return { ...base, heading:"", cards:[{front:"Term",back:"Definition"}] };
  if (type === "hotspot")   return { ...base, src:"", hotspots:[{x:25,y:25,label:"Hotspot 1",body:""},{x:65,y:55,label:"Hotspot 2",body:""}] };
  if (type === "matching")  return { ...base, instruction:"Match each item to the correct answer", pairs:[{left:"Item A",right:"Match A"},{left:"Item B",right:"Match B"},{left:"Item C",right:"Match C"}] };
  return base;
}

function extractMediaPaths(design) {
  const paths = new Set();
  for (const page of design?.pages || []) {
    for (const block of page?.blocks || []) {
      if (block.mediaBlobPath) paths.add(block.mediaBlobPath);
    }
  }
  return paths;
}

function isQuizBlock(b) { return ["quiz_mc","quiz_ms","quiz_tf","quiz_dd"].includes(b.type); }

// ── Mini WYSIWYG ──────────────────────────────────────────────────────────────
function MiniEditor({ value, onChange }) {
  const ref = useRef(null);
  const sup = useRef(false);
  useEffect(() => {
    if (ref.current) { sup.current = true; ref.current.innerHTML = value || ""; sup.current = false; }
  }, []); // eslint-disable-line
  const exec = (cmd, arg) => { ref.current?.focus(); document.execCommand(cmd, false, arg); if (ref.current) onChange(ref.current.innerHTML); };
  const TB = ({ title, children, cmd, arg }) => (
    <button type="button" className="editor-toolbar-btn" title={title} onMouseDown={e => { e.preventDefault(); exec(cmd, arg); }}>{children}</button>
  );
  return (
    <div className="html-editor">
      <div className="editor-toolbar" style={{ padding:"4px 8px" }}>
        <div className="editor-toolbar-group">
          <TB cmd="bold"><strong style={{fontSize:13}}>B</strong></TB>
          <TB cmd="italic"><em style={{fontSize:13}}>I</em></TB>
          <TB cmd="underline"><u style={{fontSize:13}}>U</u></TB>
        </div>
        <div className="editor-toolbar-sep"/>
        <div className="editor-toolbar-group">
          <TB cmd="formatBlock" arg="h2"><span style={{fontSize:11,fontWeight:700}}>H2</span></TB>
          <TB cmd="formatBlock" arg="h3"><span style={{fontSize:11,fontWeight:700}}>H3</span></TB>
          <TB cmd="formatBlock" arg="p"><span style={{fontSize:11}}>¶</span></TB>
        </div>
        <div className="editor-toolbar-sep"/>
        <TB cmd="insertUnorderedList">• List</TB>
      </div>
      <div ref={ref} className="editor-content" style={{ minHeight:80 }} contentEditable suppressContentEditableWarning
        onInput={() => { if (!sup.current && ref.current) onChange(ref.current.innerHTML); }} />
    </div>
  );
}

// ── Media uploader ────────────────────────────────────────────────────────────
function MediaUploader({ courseId, accept, limitMB, label, currentUrl, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res  = await fetch(`/api/courses/${courseId}/media`, { method:"POST", body:fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onUploaded(data.proxyUrl, data.blobName);
    } catch (e) { setError(e.message); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {currentUrl && (
        <div style={{ fontSize:12, color:"var(--green)", display:"flex", alignItems:"center", gap:6 }}>
          ✓ {label} uploaded
          <button type="button" className="btn-ghost" style={{ fontSize:12, padding:"1px 6px", color:"var(--red)" }} onClick={() => onUploaded("", "")}>Remove</button>
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <input ref={inputRef} type="file" accept={accept} style={{ display:"none" }} onChange={handleFile} />
        <button type="button" className="btn-secondary" style={{ fontSize:12, padding:"4px 10px" }}
          onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? "Uploading…" : currentUrl ? `Replace ${label}` : `Upload ${label}`}
        </button>
        <span style={{ fontSize:11, color:"var(--gray-400)" }}>Max {limitMB}MB</span>
        {error && <span style={{ fontSize:12, color:"var(--red)" }}>{error}</span>}
      </div>
    </div>
  );
}

// ── Reusable mandatory checkbox for block editors ─────────────────────────────
function MandatoryCheckbox({ label, field, block, set }) {
  return (
    <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontWeight:400, fontSize:12, marginTop:12, padding:"8px 10px", background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:6 }}>
      <input type="checkbox" checked={!!block[field]} onChange={e=>set(field,e.target.checked)} style={{ width:"auto", accentColor:"#EA580C", flexShrink:0 }}/>
      <span style={{ color:"#9A3412", fontWeight:500 }}>⚠ {label}</span>
    </label>
  );
}

// ── Block editors ─────────────────────────────────────────────────────────────
function BlockEditor({ block, courseId, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, isFinalQuizPage }) {
  const set = useCallback((k, v) => onChange({ ...block, [k]: v }), [block, onChange]);

  const header = (
    <div className="block-editor-header">
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span className="block-type-badge">{BLOCK_TYPES[block.type]?.icon} {BLOCK_TYPES[block.type]?.label}</span>
        {isQuizBlock(block) && isFinalQuizPage && (
          <div style={{ display:"flex", gap:8, fontSize:12 }}>
            <label style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", fontWeight:400, margin:0 }}>
              <input type="checkbox" checked={!!block.alwaysShow} onChange={e => set("alwaysShow", e.target.checked)}
                style={{ width:"auto", accentColor:"var(--red)" }} />
              Always show
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", fontWeight:400, margin:0 }}>
              <input type="checkbox" checked={block.alwaysShow ? false : !!block.inBank} onChange={e => set("inBank", e.target.checked)}
                disabled={block.alwaysShow} style={{ width:"auto", accentColor:"var(--red)" }} />
              In rotation pool
            </label>
          </div>
        )}
        {isQuizBlock(block) && !isFinalQuizPage && (block.type==="quiz_mc"||block.type==="quiz_ms"||block.type==="quiz_tf") && (
          <label style={{ display:"flex", alignItems:"center", gap:4, cursor:"pointer", fontWeight:400, margin:0, fontSize:12 }}>
            <input type="checkbox" checked={!!block.mustBeCorrect} onChange={e => set("mustBeCorrect", e.target.checked)}
              style={{ width:"auto", accentColor:"var(--red)" }} />
            <span style={{ color:"var(--red-dark)", fontWeight:500 }}>Must answer correctly before proceeding</span>
          </label>
        )}
      </div>
      <div style={{ display:"flex", gap:4 }}>
        <button type="button" className="btn-ghost" style={{ padding:"2px 7px" }} onClick={onMoveUp}  disabled={isFirst}>↑</button>
        <button type="button" className="btn-ghost" style={{ padding:"2px 7px" }} onClick={onMoveDown} disabled={isLast}>↓</button>
        <button type="button" className="btn-ghost" style={{ padding:"2px 8px", color:"var(--red)" }} onClick={onDelete}>✕</button>
      </div>
    </div>
  );

  const body = (() => {
    if (block.type === "text") return (
      <div style={{ padding:"12px" }}>
        <MiniEditor value={block.html||""} onChange={v => set("html", v)} />
      </div>
    );

    if (block.type === "image") return (
      <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
        <div className="form-group" style={{ margin:0 }}>
          <label>Image URL</label>
          <input value={block.src||""} onChange={e => set("src", e.target.value)} placeholder="https://…  (or upload below)" />
        </div>
        <div style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center" }}>— or —</div>
        <MediaUploader courseId={courseId} accept="image/*" limitMB={10} label="Image"
          currentUrl={block.mediaBlobPath ? `/api/courses/${courseId}/media/${encodeURIComponent(block.mediaBlobPath)}` : ""}
          onUploaded={(url, blob) => onChange({ ...block, src: url, mediaBlobPath: blob })} />
        <div className="form-group" style={{ margin:0 }}>
          <label>Alt text / Caption</label>
          <input value={block.alt||""} onChange={e => set("alt", e.target.value)} placeholder="Describe the image" />
        </div>
      </div>
    );

    if (block.type === "video") return (
      <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
        <div className="form-group" style={{ margin:0 }}>
          <label>YouTube or Vimeo URL <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(recommended — no storage cost)</span></label>
          <input value={block.src||""} onChange={e => set("src", e.target.value)}
            placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…" />
        </div>
        <div style={{ fontSize:12, color:"var(--gray-400)", textAlign:"center" }}>— or upload a private video (max 200MB) —</div>
        <MediaUploader courseId={courseId} accept="video/*" limitMB={200} label="Video"
          currentUrl={block.mediaBlobPath ? "uploaded" : ""}
          onUploaded={(url, blob) => onChange({ ...block, src: url, mediaBlobPath: blob })} />
        <div style={{ fontSize:12, padding:"8px 10px", background:"var(--red-light)", borderRadius:"var(--radius)", color:"var(--red-dark)" }}>
          💡 YouTube/Vimeo is free and has no storage cost. Upload only for private videos that can't be hosted externally.
        </div>
        <div className="form-row" style={{ margin:0 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Caption (optional)</label>
            <input value={block.caption||""} onChange={e => set("caption", e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontWeight:400, marginBottom:0, paddingBottom:8 }}>
              <input type="checkbox" checked={!!block.mustWatch} onChange={e => set("mustWatch", e.target.checked)}
                style={{ width:"auto", accentColor:"var(--red)" }} />
              <span style={{ fontWeight:500 }}>Must watch in full</span>
              <span style={{ fontSize:11, color:"var(--gray-500)" }}>(blocks advancing)</span>
            </label>
          </div>
        </div>
      </div>
    );

    if (block.type === "info") return (
      <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
        <div className="form-row" style={{ margin:0 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Heading</label>
            <input value={block.heading||""} onChange={e => set("heading", e.target.value)} placeholder="Did you know?" />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Style</label>
            <select value={block.style||"info"} onChange={e => set("style", e.target.value)}>
              <option value="info">ℹ Info (blue)</option>
              <option value="tip">💡 Tip (green)</option>
              <option value="warning">⚠ Warning (amber)</option>
              <option value="important">🔴 Important (red)</option>
            </select>
          </div>
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label>Body</label>
          <textarea value={block.body||""} onChange={e => set("body", e.target.value)} rows={3} />
        </div>
      </div>
    );

    if (block.type === "keypoints") return (
      <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
        <div className="form-group" style={{ margin:0 }}>
          <label>Heading</label>
          <input value={block.heading||""} onChange={e => set("heading", e.target.value)} placeholder="Key Points" />
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label>Points (one per line)</label>
          <textarea value={(block.points||[]).join("\n")} onChange={e => set("points", e.target.value.split("\n"))} rows={5} placeholder={"Point one\nPoint two\nPoint three"} />
        </div>
      </div>
    );

    if (block.type === "quiz_mc" || block.type === "quiz_ms" || block.type === "quiz_tf") {
      const isTF = block.type === "quiz_tf";
      const isMS = block.type === "quiz_ms";
      const options = block.options || [{text:"",correct:true},{text:"",correct:false},{text:"",correct:false},{text:"",correct:false}];
      const correctCount = options.filter(o => o.correct).length;
      return (
        <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Question</label>
            <input value={block.question||""} onChange={e => set("question", e.target.value)} placeholder="Type your question…" />
          </div>
          {!isTF && (
            <div className="form-group" style={{ margin:0 }}>
              <label>
                Answer options{" "}
                <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>
                  {isMS ? `(tick all correct answers — ${correctCount} marked correct)` : "(tick the correct answer)"}
                </span>
              </label>
              {options.map((opt, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  {isMS ? (
                    <input type="checkbox" checked={!!opt.correct}
                      onChange={() => { const o=[...options]; o[i]={...o[i],correct:!o[i].correct}; set("options",o); }}
                      style={{ width:"auto", accentColor:"var(--red)" }} />
                  ) : (
                    <input type="radio" name={`correct-${block.id}`} checked={opt.correct}
                      onChange={() => set("options", options.map((o,j) => ({ ...o, correct: j===i })))}
                      style={{ width:"auto", accentColor:"var(--red)" }} />
                  )}
                  <input value={opt.text} onChange={e => { const o=[...options]; o[i]={...o[i],text:e.target.value}; set("options",o); }} placeholder={`Option ${i+1}`} style={{ flex:1 }} />
                  {options.length > 2 && <button type="button" className="btn-ghost" style={{ padding:"2px 8px", color:"var(--red)", fontSize:13 }} onClick={() => set("options", options.filter((_,j)=>j!==i))}>✕</button>}
                </div>
              ))}
              {options.length < 8 && <button type="button" className="btn-secondary" style={{ fontSize:12, padding:"4px 10px" }}
                onClick={() => set("options", [...options, {text:"",correct:false}])}>+ Add Option</button>}
              {isMS && (
                <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8 }}>
                  <label style={{ fontSize:13, fontWeight:500, margin:0 }}>Required selections to submit:</label>
                  <input type="number" value={block.requiredCount??correctCount} min={1} max={options.length}
                    onChange={e => set("requiredCount", parseInt(e.target.value)||correctCount)}
                    style={{ width:60 }} />
                  <span style={{ fontSize:12, color:"var(--gray-400)" }}>(defaults to number of correct answers)</span>
                </div>
              )}
            </div>
          )}
          {isTF && (
            <div className="form-group" style={{ margin:0 }}>
              <label>Correct answer</label>
              <div style={{ display:"flex", gap:12 }}>
                {["True","False"].map((v,i) => (
                  <label key={v} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontWeight:400 }}>
                    <input type="radio" name={`tf-${block.id}`} checked={block.correct===i} onChange={() => set("correct",i)} style={{ width:"auto", accentColor:"var(--red)" }} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="form-row" style={{ margin:0 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label>Explanation <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(shown after answering)</span></label>
              <textarea value={block.explanation||""} onChange={e => set("explanation", e.target.value)} rows={2} />
            </div>
            {isFinalQuizPage && (
              <div className="form-group" style={{ margin:0 }}>
                <label>Point value</label>
                <input type="number" value={block.points??1} min={0} max={10}
                  onChange={e => set("points", parseInt(e.target.value)||1)} style={{ width:80 }} />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (block.type === "quiz_dd") {
      const categories = block.categories || ["Category A","Category B"];
      const items      = block.items      || [];
      return (
        <div style={{ padding:"12px", display:"flex", flexDirection:"column", gap:10 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Instruction</label>
            <input value={block.instruction||""} onChange={e => set("instruction", e.target.value)} placeholder="Drag each item to the correct category" />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Categories <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(columns users drag into)</span></label>
            {categories.map((cat, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <input value={cat} onChange={e => { const c=[...categories]; c[i]=e.target.value; set("categories",c); }} placeholder={`Category ${i+1}`} style={{ flex:1 }} />
                {categories.length > 2 && <button type="button" className="btn-ghost" style={{ padding:"2px 8px", color:"var(--red)", fontSize:13 }} onClick={() => {
                  const newCats = categories.filter((_,j)=>j!==i);
                  const newItems = items.map(it => ({ ...it, category: Math.min(it.category, newCats.length-1) }));
                  onChange({ ...block, categories:newCats, items:newItems });
                }}>✕</button>}
              </div>
            ))}
            {categories.length < 5 && <button type="button" className="btn-secondary" style={{ fontSize:12, padding:"4px 10px" }}
              onClick={() => set("categories", [...categories, `Category ${categories.length+1}`])}>+ Add Category</button>}
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Items to sort <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(set which category each belongs to)</span></label>
            {items.map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <input value={item.text} onChange={e => { const it=[...items]; it[i]={...it[i],text:e.target.value}; set("items",it); }} placeholder={`Item ${i+1}`} style={{ flex:1 }} />
                <select value={item.category} onChange={e => { const it=[...items]; it[i]={...it[i],category:parseInt(e.target.value)}; set("items",it); }} style={{ width:140 }}>
                  {categories.map((cat,ci) => <option key={ci} value={ci}>{cat}</option>)}
                </select>
                {items.length > 2 && <button type="button" className="btn-ghost" style={{ padding:"2px 8px", color:"var(--red)", fontSize:13 }} onClick={() => set("items", items.filter((_,j)=>j!==i))}>✕</button>}
              </div>
            ))}
            <button type="button" className="btn-secondary" style={{ fontSize:12, padding:"4px 10px" }}
              onClick={() => set("items", [...items, {text:"",category:0}])}>+ Add Item</button>
          </div>
          <div className="form-row" style={{ margin:0 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label>Explanation <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(shown after submitting)</span></label>
              <textarea value={block.explanation||""} onChange={e => set("explanation", e.target.value)} rows={2} />
            </div>
            {isFinalQuizPage && (
              <div className="form-group" style={{ margin:0 }}>
                <label>Point value</label>
                <input type="number" value={block.points??1} min={0} max={10}
                  onChange={e => set("points", parseInt(e.target.value)||1)} style={{ width:80 }} />
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Quote / Testimonial ───────────────────────────────────────────────────
    if (block.type === "quote") return (
      <div>
        <div className="form-group">
          <label>Quote Text</label>
          <textarea value={block.text||""} onChange={e=>set("text",e.target.value)} rows={3} placeholder="Enter the quote or testimonial…"/>
        </div>
        <div style={{display:"flex",gap:12}}>
          <div className="form-group" style={{flex:1}}><label>Attribution (name)</label><input value={block.attribution||""} onChange={e=>set("attribution",e.target.value)} placeholder="Jane Smith"/></div>
          <div className="form-group" style={{flex:1}}><label>Role / Title</label><input value={block.role||""} onChange={e=>set("role",e.target.value)} placeholder="Head of Compliance"/></div>
        </div>
      </div>
    );

    // ── Table ─────────────────────────────────────────────────────────────────
    if (block.type === "table") {
      const headers = block.headers || ["Column 1","Column 2"];
      const rows    = block.rows    || [["",""]];

      // All mutations go through a single onChange to avoid stale closure bugs
      const mutate = (newHeaders, newRows) =>
        onChange({ ...block, headers: newHeaders, rows: newRows });

      const addCol = () => {
        const nh = [...headers, "New Column"];
        const nr = rows.map(r => [...r, ""]);           // add cell to EVERY existing row
        mutate(nh, nr);
      };
      const delCol = i => {
        const nh = headers.filter((_,j) => j !== i);
        const nr = rows.map(r => r.filter((_,j) => j !== i));
        mutate(nh, nr);
      };
      const addRow = () => {
        const nr = [...rows, headers.map(() => "")];    // use current headers length
        mutate(headers, nr);
      };
      const delRow = i => mutate(headers, rows.filter((_,j) => j !== i));
      const setCell = (ri, ci, v) => {
        const nr = rows.map((r,rr) => r.map((c,cc) => rr===ri && cc===ci ? v : c));
        mutate(headers, nr);
      };
      const setHeader = (i, v) => {
        const nh = headers.map((h,j) => j===i ? v : h);
        mutate(nh, rows);
      };

      return (
        <div>
          <div className="form-group"><label>Heading (optional)</label><input value={block.heading||""} onChange={e=>set("heading",e.target.value)} placeholder="e.g. Comparison"/></div>
          <div style={{overflowX:"auto",marginTop:4}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead><tr>
                {headers.map((h,i)=>(
                  <th key={i} style={{padding:4,border:"1px solid var(--gray-200)",background:"var(--gray-50)"}}>
                    <div style={{display:"flex",gap:4,alignItems:"center"}}>
                      <input value={h} onChange={e=>setHeader(i,e.target.value)} style={{fontSize:12,padding:"2px 4px",flex:1,minWidth:60}}/>
                      {headers.length>2&&<button type="button" className="btn-ghost" style={{padding:"1px 5px",color:"var(--red)",fontSize:11}} onClick={()=>delCol(i)}>✕</button>}
                    </div>
                  </th>
                ))}
                <th style={{width:30}}/>
              </tr></thead>
              <tbody>{rows.map((row,ri)=>(
                <tr key={ri}>
                  {headers.map((_,ci)=>(
                    <td key={ci} style={{padding:4,border:"1px solid var(--gray-200)"}}>
                      <input value={row[ci]??""} onChange={e=>setCell(ri,ci,e.target.value)} style={{fontSize:12,padding:"2px 4px",width:"100%"}}/>
                    </td>
                  ))}
                  <td style={{padding:4}}>{rows.length>1&&<button type="button" className="btn-ghost" style={{padding:"1px 5px",color:"var(--red)",fontSize:11}} onClick={()=>delRow(ri)}>✕</button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px"}} onClick={addRow}>+ Row</button>
            <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px"}} onClick={addCol}>+ Column</button>
          </div>
        </div>
      );
    }

    // ── Process Steps ─────────────────────────────────────────────────────────
    if (block.type === "process") {
      const steps = block.steps || [];
      return (
        <div>
          <div className="form-group"><label>Heading (optional)</label><input value={block.heading||""} onChange={e=>set("heading",e.target.value)} placeholder="e.g. How to raise a complaint"/></div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {steps.map((step,i)=>(
              <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                <div style={{width:26,height:26,borderRadius:"50%",background:"var(--gray-700)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:4}}>{i+1}</div>
                <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                  <input value={step.title||""} onChange={e=>{const s=[...steps];s[i]={...s[i],title:e.target.value};set("steps",s);}} placeholder={`Step ${i+1} title`}/>
                  <textarea value={step.body||""} onChange={e=>{const s=[...steps];s[i]={...s[i],body:e.target.value};set("steps",s);}} rows={2} placeholder="Description…"/>
                </div>
                {steps.length>1&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12,flexShrink:0}} onClick={()=>set("steps",steps.filter((_,j)=>j!==i))}>✕</button>}
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("steps",[...steps,{title:`Step ${steps.length+1}`,body:""}])}>+ Add Step</button>
          <MandatoryCheckbox label="Must expand all steps before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── Scenario / Branching ──────────────────────────────────────────────────
    if (block.type === "scenario") {
      const choices = block.choices || [];
      return (
        <div>
          <div className="form-group"><label>Situation</label><textarea value={block.situation||""} onChange={e=>set("situation",e.target.value)} rows={3} placeholder="Describe the scenario the learner faces…"/></div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {choices.map((c,i)=>(
              <div key={i} style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                  <span style={{fontWeight:700,fontSize:13,color:"var(--gray-500)",flexShrink:0}}>Option {String.fromCharCode(65+i)}</span>
                  <input value={c.label||""} onChange={e=>{const ch=[...choices];ch[i]={...ch[i],label:e.target.value};set("choices",ch);}} placeholder="What the learner can choose…" style={{flex:1}}/>
                  <select value={c.outcome||"neutral"} onChange={e=>{const ch=[...choices];ch[i]={...ch[i],outcome:e.target.value};set("choices",ch);}} style={{width:110,fontSize:12}}>
                    <option value="neutral">Neutral</option>
                    <option value="good">✓ Good</option>
                    <option value="bad">✗ Bad</option>
                  </select>
                  {choices.length>2&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("choices",choices.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <textarea value={c.feedback||""} onChange={e=>{const ch=[...choices];ch[i]={...ch[i],feedback:e.target.value};set("choices",ch);}} rows={2} placeholder="Feedback shown after selecting this option…"/>
              </div>
            ))}
          </div>
          {choices.length<5&&<button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("choices",[...choices,{label:"",feedback:"",outcome:"neutral"}])}>+ Add Option</button>}
          <MandatoryCheckbox label="Must select an option before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── Flashcards ────────────────────────────────────────────────────────────
    if (block.type === "flashcard") {
      const cards = block.cards || [];
      function updateCard(i, key, val) {
        const cs = [...cards]; cs[i] = { ...cs[i], [key]: val }; set("cards", cs);
      }
      return (
        <div>
          <div className="form-group"><label>Set Heading (optional)</label><input value={block.heading||""} onChange={e=>set("heading",e.target.value)} placeholder="e.g. Key Terms"/></div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {cards.map((c,i)=>(
              <div key={i} style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:8,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--gray-500)"}}>Card {i+1}</span>
                  {cards.length>1&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("cards",cards.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <label style={{fontSize:11,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Front — Text</label>
                    <input value={c.front||""} onChange={e=>updateCard(i,"front",e.target.value)} placeholder="Term or question"/>
                    <label style={{fontSize:11,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em",marginTop:4}}>Front — Image URL (optional)</label>
                    <input value={c.frontImage||""} onChange={e=>updateCard(i,"frontImage",e.target.value)} placeholder="https://..."/>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <label style={{fontSize:11,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Back — Text</label>
                    <textarea value={c.back||""} onChange={e=>updateCard(i,"back",e.target.value)} rows={2} placeholder="Answer or definition"/>
                    <label style={{fontSize:11,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em",marginTop:4}}>Back — Image URL (optional)</label>
                    <input value={c.backImage||""} onChange={e=>updateCard(i,"backImage",e.target.value)} placeholder="https://..."/>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("cards",[...cards,{front:"",back:""}])}>+ Add Card</button>
          <MandatoryCheckbox label="Must view all cards before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── Hotspot ───────────────────────────────────────────────────────────────
    if (block.type === "hotspot") {
      const hotspots = block.hotspots || [];
      return (
        <div>
          <div className="form-group"><label>Image URL</label><input value={block.src||""} onChange={e=>set("src",e.target.value)} placeholder="https://…"/></div>
          <p style={{fontSize:12,color:"var(--gray-400)",marginTop:-4,marginBottom:8}}>Set X% and Y% positions from the top-left of the image (0–100).</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {hotspots.map((h,i)=>(
              <div key={i} style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                  <input value={h.label||""} onChange={e=>{const hs=[...hotspots];hs[i]={...hs[i],label:e.target.value};set("hotspots",hs);}} placeholder="Label" style={{flex:1}}/>
                  <label style={{fontSize:12,flexShrink:0}}>X%</label>
                  <input type="number" value={h.x??50} min={0} max={100} onChange={e=>{const hs=[...hotspots];hs[i]={...hs[i],x:parseInt(e.target.value)||0};set("hotspots",hs);}} style={{width:80}}/>
                  <label style={{fontSize:12,flexShrink:0}}>Y%</label>
                  <input type="number" value={h.y??50} min={0} max={100} onChange={e=>{const hs=[...hotspots];hs[i]={...hs[i],y:parseInt(e.target.value)||0};set("hotspots",hs);}} style={{width:80}}/>
                  {hotspots.length>1&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("hotspots",hotspots.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <textarea value={h.body||""} onChange={e=>{const hs=[...hotspots];hs[i]={...hs[i],body:e.target.value};set("hotspots",hs);}} rows={2} placeholder="Text revealed when this hotspot is clicked…"/>
              </div>
            ))}
          </div>
          {hotspots.length<8&&<button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("hotspots",[...hotspots,{x:50,y:50,label:"",body:""}])}>+ Add Hotspot</button>}
          <MandatoryCheckbox label="Must click all hotspots before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── Matching Pairs ────────────────────────────────────────────────────────
    if (block.type === "matching") {
      const pairs = block.pairs || [];
      return (
        <div>
          <div className="form-group"><label>Instruction</label><input value={block.instruction||""} onChange={e=>set("instruction",e.target.value)} placeholder="Match each item to the correct answer"/></div>
          <div style={{display:"flex",gap:8,marginBottom:4}}>
            <div style={{flex:1,fontSize:11,fontWeight:600,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Left (item)</div>
            <div style={{flex:1,fontSize:11,fontWeight:600,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Right (match)</div>
            <div style={{width:30}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {pairs.map((p,i)=>(
              <div key={i} style={{display:"flex",gap:8,alignItems:"center"}}>
                <input value={p.left||""} onChange={e=>{const ps=[...pairs];ps[i]={...ps[i],left:e.target.value};set("pairs",ps);}} placeholder={`Item ${i+1}`} style={{flex:1}}/>
                <input value={p.right||""} onChange={e=>{const ps=[...pairs];ps[i]={...ps[i],right:e.target.value};set("pairs",ps);}} placeholder={`Match ${i+1}`} style={{flex:1}}/>
                {pairs.length>2&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("pairs",pairs.filter((_,j)=>j!==i))}>✕</button>}
              </div>
            ))}
          </div>
          {pairs.length<8&&<button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("pairs",[...pairs,{left:"",right:""}])}>+ Add Pair</button>}
          <MandatoryCheckbox label="Must get all pairs correct before proceeding" field="mustBeCorrect" block={block} set={set}/>
        </div>
      );
    }

    // ── Flowchart ─────────────────────────────────────────────────────────────
    if (block.type === "flowchart") {
      const steps = block.steps || [];
      return (
        <div>
          <p style={{fontSize:12,color:"var(--gray-400)",marginBottom:8}}>Each step is connected by an arrow. Add or remove steps as needed.</p>
          <div style={{display:"flex",flexDirection:"column",gap:0}}>
            {steps.map((s,i)=>(
              <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                <div style={{width:"100%",display:"flex",gap:8,alignItems:"flex-start",background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                  <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                    <input value={s.label||""} onChange={e=>{const ns=[...steps];ns[i]={...ns[i],label:e.target.value};set("steps",ns);}} placeholder={`Step ${i+1} label`} style={{fontWeight:600}}/>
                    <textarea value={s.body||""} onChange={e=>{const ns=[...steps];ns[i]={...ns[i],body:e.target.value};set("steps",ns);}} rows={1} placeholder="Optional description…"/>
                  </div>
                  {steps.length>1&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12,flexShrink:0,marginTop:4}} onClick={()=>set("steps",steps.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                {i<steps.length-1&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"2px 0"}}>
                  <div style={{width:2,height:14,background:"var(--gray-300)"}}/>
                  <div style={{width:0,height:0,borderLeft:"6px solid transparent",borderRight:"6px solid transparent",borderTop:"9px solid var(--gray-400)"}}/>
                </div>}
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("steps",[...steps,{label:`Step ${steps.length+1}`,body:""}])}>+ Add Step</button>
        </div>
      );
    }

    // ── Accordion ─────────────────────────────────────────────────────────────
    if (block.type === "accordion") {
      const items = block.items || [];
      return (
        <div>
          <div className="form-group"><label>Section Heading (optional)</label><input value={block.heading||""} onChange={e=>set("heading",e.target.value)} placeholder="e.g. Frequently Asked Questions"/></div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {items.map((item,i)=>(
              <div key={i} style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                  <input value={item.title||""} onChange={e=>{const it=[...items];it[i]={...it[i],title:e.target.value};set("items",it);}} placeholder={`Item ${i+1} title`} style={{flex:1,fontWeight:600}}/>
                  {items.length>1&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("items",items.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <textarea value={item.body||""} onChange={e=>{const it=[...items];it[i]={...it[i],body:e.target.value};set("items",it);}} rows={2} placeholder="Content shown when expanded…"/>
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("items",[...items,{title:"",body:""}])}>+ Add Item</button>
          <MandatoryCheckbox label="Must expand all items before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── Tabs ──────────────────────────────────────────────────────────────────
    if (block.type === "tabs") {
      const tabItems = block.tabs || [];
      return (
        <div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {tabItems.map((tab,i)=>(
              <div key={i} style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:6,padding:"10px 12px"}}>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                  <input value={tab.label||""} onChange={e=>{const ts=[...tabItems];ts[i]={...ts[i],label:e.target.value};set("tabs",ts);}} placeholder={`Tab ${i+1} label`} style={{flex:1,fontWeight:600}}/>
                  {tabItems.length>2&&<button type="button" className="btn-ghost" style={{padding:"2px 6px",color:"var(--red)",fontSize:12}} onClick={()=>set("tabs",tabItems.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <textarea value={tab.body||""} onChange={e=>{const ts=[...tabItems];ts[i]={...ts[i],body:e.target.value};set("tabs",ts);}} rows={3} placeholder="Content of this tab…"/>
              </div>
            ))}
          </div>
          {tabItems.length<6&&<button type="button" className="btn-secondary" style={{fontSize:12,padding:"4px 10px",marginTop:8}} onClick={()=>set("tabs",[...tabItems,{label:"",body:""}])}>+ Add Tab</button>}
          <MandatoryCheckbox label="Must view all tabs before proceeding" field="mustInteract" block={block} set={set}/>
        </div>
      );
    }

    // ── 2 / 3 Columns ─────────────────────────────────────────────────────────
    if (block.type === "columns") {
      const count = block.count || 2;
      return (
        <div>
          <div className="form-group">
            <label>Number of columns</label>
            <div style={{display:"flex",gap:12}}>
              {[2,3].map(n=>(
                <label key={n} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontWeight:400,fontSize:13}}>
                  <input type="radio" checked={count===n} onChange={()=>set("count",n)} style={{accentColor:"var(--gray-700)",width:"auto"}}/>{n} columns
                </label>
              ))}
            </div>
          </div>
          {[1,2,...(count===3?[3]:[])].map(n=>(
            <div key={n} className="form-group">
              <label>Column {n}</label>
              <textarea value={block[`col${n}`]||""} onChange={e=>set(`col${n}`,e.target.value)} rows={4} placeholder={`Column ${n} content…`}/>
            </div>
          ))}
        </div>
      );
    }

    // ── Divider ───────────────────────────────────────────────────────────────
    if (block.type === "divider") {
      return (
        <div className="form-row">
          <div className="form-group" style={{margin:0}}>
            <label>Label (optional)</label>
            <input value={block.label||""} onChange={e=>set("label",e.target.value)} placeholder="e.g. Summary"/>
          </div>
          <div className="form-group" style={{margin:0}}>
            <label>Style</label>
            <select value={block.style||"line"} onChange={e=>set("style",e.target.value)}>
              <option value="line">Simple line</option>
              <option value="thick">Thick line</option>
              <option value="dotted">Dotted</option>
              <option value="spaced">Extra spacing</option>
            </select>
          </div>
        </div>
      );
    }

    return null;
  })();

  return <div className="block-editor">{header}{body}</div>;
}

// ── Page editor ───────────────────────────────────────────────────────────────
function PageEditor({ page, courseId, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, passScore, onPassScoreChange, quizBankCount, onQuizBankCountChange, sections, onSectionChange }) {
  const [collapsed, setCollapsed] = useState(false);
  const isFinalQuiz = !!page.isFinalQuiz;
  const quizBlocks = (page.blocks||[]).filter(isQuizBlock);
  const bankBlocks = quizBlocks.filter(b => !b.alwaysShow && b.inBank);
  const alwaysBlocks = quizBlocks.filter(b => b.alwaysShow);

  function addBlock(type) {
    onChange({ ...page, blocks:[...(page.blocks||[]), newBlock(type)] });
  }
  function updateBlock(i, block) {
    const blocks=[...(page.blocks||[])]; blocks[i]=block;
    onChange({ ...page, blocks });
  }
  function deleteBlock(i) { onChange({ ...page, blocks:(page.blocks||[]).filter((_,j)=>j!==i) }); }
  function moveBlock(i, dir) {
    const blocks=[...(page.blocks||[])]; const j=i+dir;
    if (j<0||j>=blocks.length) return;
    [blocks[i],blocks[j]]=[blocks[j],blocks[i]];
    onChange({ ...page, blocks });
  }

  const isIntroPage = page.sectionId === SEC0_ID;
  const [showMoveSection, setShowMoveSection] = useState(false);

  function moveToSection(sectionId) {
    onChange({ ...page, sectionId: sectionId || null });
    setShowMoveSection(false);
  }

  return (
    <div className="page-editor" style={isFinalQuiz ? { borderColor:"var(--red)" } : isIntroPage ? { borderColor:"#9CA3AF" } : {}}>
      <div className="page-editor-header" onClick={() => setCollapsed(c=>!c)}
        style={isIntroPage ? { background:"#4B5563" } : {}}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
          <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", opacity:0.6, flexShrink:0 }}>
            {isIntroPage ? "Intro" : "Page"}
          </span>
          {/* Editable page title inline in header */}
          <input
            value={page.title||""}
            onChange={e => { e.stopPropagation(); onChange({ ...page, title:e.target.value }); }}
            onClick={e => e.stopPropagation()}
            placeholder="Untitled Page"
            style={{ flex:1, minWidth:0, fontSize:14, fontWeight:700, padding:"2px 7px",
              background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)",
              borderRadius:5, color:"#fff", outline:"none" }}
          />
          {isFinalQuiz && <span style={{ fontSize:10, fontWeight:700, background:"var(--red)", color:"#fff", padding:"2px 7px", borderRadius:99, flexShrink:0 }}>🏆 Final Quiz</span>}
          <span style={{ fontSize:11, opacity:0.55, flexShrink:0 }}>{(page.blocks||[]).length} block{(page.blocks||[]).length!==1?"s":""}</span>
        </div>
        <div style={{ display:"flex", gap:4, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
          {!isIntroPage && (
            <>
              <button type="button" className="btn-ghost" style={{ padding:"2px 7px", color:"rgba(255,255,255,0.7)" }} onClick={onMoveUp}  disabled={isFirst}>↑</button>
              <button type="button" className="btn-ghost" style={{ padding:"2px 7px", color:"rgba(255,255,255,0.7)" }} onClick={onMoveDown} disabled={isLast}>↓</button>
              {/* Move to section */}
              {sections && sections.length > 1 && (
                <div style={{ position:"relative" }}>
                  <button type="button" className="btn-ghost"
                    style={{ fontSize:11, padding:"2px 8px", color:"rgba(255,255,255,0.7)" }}
                    onClick={e=>{ e.stopPropagation(); setShowMoveSection(s=>!s); }}>
                    ⇄ Section
                  </button>
                  {showMoveSection && (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", right:0, zIndex:100,
                      background:"#fff", border:"1px solid var(--gray-200)", borderRadius:8,
                      boxShadow:"0 8px 24px rgba(0,0,0,0.15)", padding:"6px", minWidth:180 }}>
                      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-400)", padding:"4px 8px 6px" }}>
                        Move to section
                      </div>
                      {sections.map(s => (
                        <button key={s.id} type="button"
                          onClick={()=>moveToSection(s.id)}
                          style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 10px",
                            fontSize:13, borderRadius:6, background: page.sectionId===s.id ? "var(--gray-100)" : "none",
                            fontWeight: page.sectionId===s.id ? 700 : 400,
                            color:"var(--gray-800)", border:"none", cursor:"pointer" }}>
                          {page.sectionId===s.id ? "✓ " : ""}{s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button type="button" className="btn-ghost" style={{ fontSize:12, padding:"3px 10px", color: isFinalQuiz ? "#FCA5A5" : "rgba(255,255,255,0.7)" }}
                onClick={e => { e.stopPropagation(); onChange({ ...page, isFinalQuiz: !isFinalQuiz }); }}>
                {isFinalQuiz ? "Remove Quiz Flag" : "🏆 Final Quiz"}
              </button>
              <button type="button" className="btn-ghost" style={{ padding:"2px 8px", color:"#FCA5A5" }} onClick={onDelete}>Delete</button>
            </>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="page-editor-body">
          {isFinalQuiz && (
            <div style={{ background:"var(--red-light)", border:"1px solid var(--red)", borderRadius:"var(--radius)", padding:"12px 16px", marginBottom:12, fontSize:13 }}>
              <div style={{ fontWeight:600, marginBottom:8, color:"var(--red-dark)" }}>🏆 Final Quiz Settings</div>
              <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, fontWeight:400, margin:0 }}>
                  Pass score:
                  <input type="number" value={passScore} min={1} max={100} onChange={e => onPassScoreChange(parseInt(e.target.value)||80)}
                    style={{ width:60 }} />
                  %
                </label>
                <label style={{ display:"flex", alignItems:"center", gap:8, fontWeight:400, margin:0 }}>
                  Questions shown per attempt:
                  <input type="number" value={quizBankCount} min={1} max={bankBlocks.length||20}
                    onChange={e => onQuizBankCountChange(parseInt(e.target.value)||5)} style={{ width:60 }} />
                  <span style={{ color:"var(--gray-500)", fontSize:12 }}>({bankBlocks.length} in pool, {alwaysBlocks.length} always shown)</span>
                </label>
              </div>
              <p style={{ margin:"8px 0 0", color:"var(--gray-600)", fontSize:12 }}>
                Mark each quiz block as <strong>Always show</strong> or <strong>In rotation pool</strong> using the checkboxes on each question. Pool questions are randomly selected each attempt.
              </p>
            </div>
          )}

          {(page.blocks||[]).map((block,i) => (
            <BlockEditor key={block.id} block={block} courseId={courseId}
              onChange={b => updateBlock(i,b)} onDelete={() => deleteBlock(i)}
              onMoveUp={() => moveBlock(i,-1)} onMoveDown={() => moveBlock(i,1)}
              isFirst={i===0} isLast={i===(page.blocks||[]).length-1}
              isFinalQuizPage={isFinalQuiz} />
          ))}

          <div style={{ borderTop:"1px solid var(--gray-200)", marginTop:16, paddingTop:14 }}>
            <div style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--gray-400)", marginBottom:10 }}>Add Block</div>
            {BLOCK_GROUPS.map(group => {
              const groupTypes = Object.entries(BLOCK_TYPES).filter(([, bt]) => bt.group === group.id);
              const isQuizGroup = group.id === "questions";
              return (
                <div key={group.id} style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <div style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em",
                      color:group.color, paddingLeft:6, borderLeft:`3px solid ${group.color}` }}>
                      {group.label}
                    </div>
                    {isQuizGroup && (
                      <span style={{ fontSize:10, fontWeight:700, background:"#FEF3C7", color:"#92400E",
                        border:"1px solid #FDE68A", borderRadius:99, padding:"1px 7px" }}>
                        🏆 Count toward quiz score
                      </span>
                    )}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5 }}>
                    {groupTypes.map(([type, {label, icon, desc}]) => (
                      <button key={type} type="button"
                        onClick={() => addBlock(type)}
                        style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:3,
                          padding:"8px 10px", borderRadius:7, cursor:"pointer", textAlign:"left",
                          background:"var(--gray-50)", border:`1.5px solid var(--gray-200)`,
                          borderLeft:`3px solid ${group.color}`, transition:"all 0.12s",
                          width:"100%", position:"relative" }}
                        onMouseEnter={e=>{ e.currentTarget.style.background="#fff"; e.currentTarget.style.borderColor=group.color; e.currentTarget.style.boxShadow=`0 2px 8px rgba(0,0,0,0.08)`; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background="var(--gray-50)"; e.currentTarget.style.borderColor="var(--gray-200)"; e.currentTarget.style.borderLeftColor=group.color; e.currentTarget.style.boxShadow="none"; }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, width:"100%" }}>
                          <span style={{ fontSize:15, flexShrink:0 }}>{icon}</span>
                          <span style={{ fontSize:12, fontWeight:700, color:"var(--gray-800)", lineHeight:1.2 }}>{label}</span>
                        </div>
                        <span style={{ fontSize:11, color:"var(--gray-500)", lineHeight:1.3 }}>{desc}</span>
                      </button>
                    ))}
                  </div>
                  {isQuizGroup && (
                    <div style={{ fontSize:11, color:"#92400E", background:"#FFFBEB", border:"1px solid #FDE68A",
                      borderRadius:6, padding:"6px 10px", marginTop:6, lineHeight:1.5 }}>
                      <strong>Final Quiz:</strong> only blocks from this group count toward the quiz score and pass/fail threshold. Add them to a page marked 🏆 Final Quiz.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main CourseDesigner ───────────────────────────────────────────────────────
export default function CourseDesigner({ courseId, courseName, onClose, onDirtyChange }) {
  const [design,     setDesign]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [loadError,  setLoadError]  = useState(null); // permanent: blocks saving
  const [error,      setError]      = useState(null); // transient: cleared on next save attempt
  const [preview,    setPreview]    = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [isDirty,    setIsDirty]    = useState(false);
  const [completionMode, setCompletionMode] = useState("last_page");
  const savedDesignRef = useRef(null);

  // Track dirty state whenever design changes
  function updateDesign(newDesign) {
    setDesign(newDesign);
    setIsDirty(true);
    if (onDirtyChange) onDirtyChange(true);
  }

  useEffect(() => {
    Promise.all([
      fetch(`/api/courses/${courseId}/design`).then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || `Failed to load design (${r.status})`);
        return d;
      }),
      fetch(`/api/courses/${courseId}`).then(r => r.json()).catch(() => ({})),
    ]).then(([d, course]) => {
      const isNewCourse = !d.courseDesign; // null = genuinely new, never an error (errors throw above)
      let design = d.courseDesign || DEFAULT_DESIGN;

      // Migrate: ensure sections array exists
      if (!design.sections) design = { ...design, sections: [{ id:SEC1_ID, title:"Section 1" }] };
      // Migrate: ensure introEnabled field exists
      if (design.introEnabled === undefined) design = { ...design, introEnabled: true };
      // Migrate: ensure intro page exists (sectionId === SEC0_ID)
      const hasIntro = (design.pages||[]).some(p => p.sectionId === SEC0_ID);
      if (!hasIntro) {
        design = { ...design, pages: [
          { id:"page_intro", title:"Introduction", blocks:[{ id:"block_intro_text", type:"text", html:"<p>Welcome to this course.</p>" }], isFinalQuiz:false, sectionId:SEC0_ID },
          ...(design.pages||[])
        ]};
      } else {
        // Ensure intro page has at least one block
        design = { ...design, pages: (design.pages||[]).map(p =>
          p.sectionId === SEC0_ID && (!p.blocks || p.blocks.length === 0)
            ? { ...p, blocks:[{ id:"block_intro_text_"+ Date.now(), type:"text", html:"<p>Welcome to this course.</p>" }] }
            : p
        )};
      }
      // Only inject Section 1 into brand new courses — don't disrupt existing section structures
      if (isNewCourse && !design.sections.some(s => s.id === SEC1_ID)) {
        design = { ...design, sections: [{ id:SEC1_ID, title:"Section 1" }, ...design.sections] };
      }
      // Clean up: remove any injected section_1 that has no pages (from a previous bad migration)
      if (!isNewCourse) {
        const sec1HasPages = (design.pages||[]).some(p => p.sectionId === SEC1_ID);
        if (!sec1HasPages) {
          design = { ...design, sections: (design.sections||[]).filter(s => s.id !== SEC1_ID) };
        }
      }
      setDesign(design);
      setCompletionMode(course.completionMode || "last_page");
    }).catch(e => {
      setLoadError(`Could not load course design: ${e.message}`);
    }).finally(() => setLoading(false));
  }, [courseId]);

  async function save() {
    if (loadError) { alert("Cannot save: the course design failed to load correctly. Refresh and try again."); return; }

    // Validate final quiz requirement
    const requiresQuiz = completionMode === "pass_quiz" || completionMode === "last_and_pass";
    if (requiresQuiz) {
      const finalQuizPage = (design?.pages||[]).find(p => p.isFinalQuiz);
      if (!finalQuizPage) {
        setError(`Completion mode "${COMPLETION_MODES.find(m=>m.value===completionMode)?.label}" requires a page marked as Final Quiz. Mark a page as 🏆 Final Quiz before saving.`);
        return;
      }
      const quizBlocks = (finalQuizPage.blocks||[]).filter(isQuizBlock);
      if (quizBlocks.length === 0) {
        setError("The Final Quiz page must contain at least one question block (Multiple Choice, Multi-Select, True/False, or Drag & Drop).");
        return;
      }
    }

    setSaving(true); setSaved(false); setError(null);
    try {
      // Sort pages into section order before saving: intro → sections in order → unsectioned
      const sectionOrder = [SEC0_ID, ...(design?.sections||[]).map(s => s.id)];
      const sortedPages = [...(design?.pages||[])].sort((a, b) => {
        const ai = sectionOrder.indexOf(a.sectionId ?? "__none__");
        const bi = sectionOrder.indexOf(b.sectionId ?? "__none__");
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
      });
      const designToSave = { ...design, pages: sortedPages };
      const r1 = await fetch(`/api/courses/${courseId}/design`, {
        method:"PUT", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ courseDesign: designToSave }),
      });
      if (!r1.ok) throw new Error((await r1.json()).error);
      await fetch(`/api/courses/${courseId}`, {
        method:"PUT", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ completionMode }),
      });
      setSaved(true);
      setIsDirty(false);
      if (onDirtyChange) onDirtyChange(false);
      onClose(); // close immediately
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function addPage() {
    updateDesign(d => ({ ...d, pages:[...(d.pages||[]), { id:`page_${Date.now()}`, title:"", blocks:[], isFinalQuiz:false }] }));
  }
  function updatePage(i, page) { setDesign(d => { const pages=[...d.pages]; pages[i]=page; return {...d,pages}; }); }
  function deletePage(i) {
    if (!window.confirm("Delete this page?")) return;
    updateDesign(d => ({ ...d, pages:d.pages.filter((_,j)=>j!==i) }));
  }
  function movePage(i, dir) {
    setDesign(d => {
      const pages=[...d.pages]; const j=i+dir;
      if (j<0||j>=pages.length) return d;
      [pages[i],pages[j]]=[pages[j],pages[i]];
      return {...d,pages};
    });
  }

  if (loading) return <div style={{ padding:48, display:"flex", justifyContent:"center" }}><span className="spinner" style={{ width:28,height:28 }}/></div>;

  if (loadError) return (
    <div style={{ padding:32, textAlign:"center" }}>
      <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
      <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:"var(--gray-900)" }}>Could not load course design</div>
      <div style={{ fontSize:14, color:"var(--gray-500)", marginBottom:20 }}>{loadError}</div>
      <button className="btn-primary" onClick={() => window.location.reload()}>Refresh page</button>
    </div>
  );
  if (preview) return <CoursePreview design={design} completionMode={completionMode} onClose={() => setPreview(false)} preview />;

  const pages = design?.pages || [];

  return (
    <div>
      
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700 }}>{courseName}</div>
          <div style={{ fontSize:13, color:"var(--gray-500)" }}>{pages.length} pages · {pages.reduce((n,p)=>n+(p.blocks||[]).length,0)} blocks</div>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {saved  && <span style={{ fontSize:13, color:"var(--green)" }}>✓ Saved</span>}
          {error  && <span style={{ fontSize:13, color:"var(--red)" }}>{error}</span>}
          <button className="btn-secondary" onClick={() => setPreview(true)}>👁 Preview</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving?"Saving…":"Save Course"}</button>
        </div>
      </div>

      {/* Settings */}
      <div className="card" style={{ marginBottom:16, padding:"16px 20px" }}>
        <div style={{ fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-400)", marginBottom:12 }}>Course Settings</div>
        <div className="form-row">
          <div className="form-group" style={{ margin:0 }}>
            <label>Course Title (shown in player)</label>
            <input value={design?.theme?.title||""} onChange={e => updateDesign(d=>({...d,theme:{...d.theme,title:e.target.value}}))} placeholder={courseName} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Subtitle</label>
            <input value={design?.theme?.subtitle||""} onChange={e => updateDesign(d=>({...d,theme:{...d.theme,subtitle:e.target.value}}))} />
          </div>
        </div>
        <div className="form-row" style={{ marginTop:10 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label>Primary Colour</label>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input type="color" value={design?.theme?.primaryColor||"#F54927"} onChange={e => updateDesign(d=>({...d,theme:{...d.theme,primaryColor:e.target.value}}))} style={{ width:44,height:36,padding:2,borderRadius:"var(--radius)",cursor:"pointer" }} />
              <input value={design?.theme?.primaryColor||"#F54927"} onChange={e => updateDesign(d=>({...d,theme:{...d.theme,primaryColor:e.target.value}}))} style={{ width:100 }} />
            </div>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Completion Mode</label>
            <select value={completionMode} onChange={e => setCompletionMode(e.target.value)}>
              {COMPLETION_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", marginTop:14, padding:"10px 14px", background: design?.enableCertificate ? "#ECFDF5" : "var(--gray-50)", borderRadius:"var(--radius)", border:`1.5px solid ${design?.enableCertificate ? "#10B981" : "var(--gray-200)"}`, transition:"all 0.15s" }}>
          <input type="checkbox" checked={!!design?.enableCertificate}
            onChange={e => updateDesign(d=>({...d, enableCertificate: e.target.checked}))}
            style={{ width:"auto", accentColor:"#10B981", flexShrink:0 }}/>
          <div>
            <div style={{ fontWeight:600, fontSize:13, color: design?.enableCertificate ? "#065F46" : "var(--gray-700)" }}>
              🏅 Issue completion certificate
            </div>
            <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:1 }}>
              Trainees can download a PDF certificate of completion when they finish this course.
            </div>
          </div>
        </label>
      </div>

      {/* Intro toggle (moved from Sections card — sections now managed inline in page list) */}
      <div className="card" style={{ marginBottom:16, padding:"14px 20px" }}>
        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
          <input type="checkbox" checked={design?.introEnabled !== false} onChange={e=>updateDesign(d=>({...d,introEnabled:e.target.checked}))} style={{ width:"auto", accentColor:"var(--gray-700)" }}/>
          <div>
            <div style={{ fontWeight:600, fontSize:13 }}>Show Introduction page</div>
            <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:1 }}>A fixed intro page before Section 1 — good for course overview, objectives, and prerequisites.</div>
          </div>
        </label>
      </div>

      {/* Pages — Introduction (Section 0) + Sectioned groups */}
      {(() => {
        const sections = design?.sections || [];
        const allPages = design?.pages || [];
        const introEnabled = design?.introEnabled !== false;

        // Intro pages (sectionId === SEC0_ID)
        const introPages = allPages.filter(p => p.sectionId === SEC0_ID);
        // Un-sectioned pages (no sectionId or sectionId not in sections list, excluding intro)
        const unsectioned = allPages.filter(p => !p.sectionId && p.sectionId !== SEC0_ID);
        // Section 0 pages that are not SEC0_ID (shouldn't exist, safety)

        const renderPageEditor = (page) => {
          const i = allPages.indexOf(page);
          return (
            <PageEditor key={page.id} page={page} courseId={courseId}
              onChange={p => updatePage(i,p)} onDelete={() => deletePage(i)}
              onMoveUp={() => movePage(i,-1)} onMoveDown={() => movePage(i,1)}
              isFirst={i===0} isLast={i===allPages.length-1}
              passScore={design?.passScore??80} onPassScoreChange={v => updateDesign(d=>({...d,passScore:v}))}
              quizBankCount={design?.quizBankCount??5} onQuizBankCountChange={v => updateDesign(d=>({...d,quizBankCount:v}))}
              sections={[...sections]}
              isIntroPage={page.sectionId === SEC0_ID}
            />
          );
        };

        return (
          <>
            {/* ── Introduction (Section 0) ─────────────────────────────── */}
            <div style={{ marginBottom:16, opacity: introEnabled ? 1 : 0.45, pointerEvents: introEnabled ? "auto" : "none" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8,
                padding:"8px 12px", background:"#4B5563", borderRadius:8 }}>
                <span style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em",
                  color:"rgba(255,255,255,0.55)", flexShrink:0 }}>SECTION</span>
                <div style={{ width:20, height:20, borderRadius:4, background:"rgba(255,255,255,0.15)",
                  color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:12, fontWeight:700, flexShrink:0 }}>0</div>
                <span style={{ fontSize:13, fontWeight:600, color:"#fff", flex:1 }}>Introduction</span>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.45)" }}>
                  Always first · {introEnabled ? "enabled" : "disabled"}
                </span>
                {!introEnabled && <span style={{ fontSize:11, background:"rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.6)", padding:"2px 8px", borderRadius:99 }}>Hidden</span>}
              </div>
              <div style={{ paddingLeft:16, borderLeft:"3px solid #9CA3AF" }}>
                {introPages.map(renderPageEditor)}
                {introPages.length === 0 && (
                  <button className="btn-secondary" style={{ width:"100%", fontSize:13, marginTop:4 }}
                    onClick={()=>updateDesign(d=>({...d, pages:[...(d.pages||[]), { id:`page_${Date.now()}`, title:"Introduction", blocks:[{ id:`b_${Date.now()}`, type:"text", html:"<p></p>" }], isFinalQuiz:false, sectionId:SEC0_ID }] }))}>
                    + Add Intro Page
                  </button>
                )}
                {introPages.length > 0 && (
                  <p style={{ fontSize:12, color:"var(--gray-400)", marginTop:6, paddingLeft:2 }}>One intro page — always first for learners.</p>
                )}
              </div>
            </div>

            {/* ── Numbered sections ────────────────────────────────────── */}
            {sections.map((sec, si) => {
              const secPages    = allPages.filter(p => p.sectionId === sec.id);
              const isFirst     = si === 0;
              const isLast      = si === sections.length - 1;
              const isProtected = sec.id === SEC1_ID;
              const SECTION_COLOR = sec.color || "#0F4C81";

              function renameSection(title) {
                updateDesign(d => ({ ...d, sections:(d.sections||[]).map(s => s.id===sec.id ? {...s,title} : s) }));
              }
              function recolorSection(color) {
                updateDesign(d => ({ ...d, sections:(d.sections||[]).map(s => s.id===sec.id ? {...s,color} : s) }));
              }
              function deleteSection() {
                if (!window.confirm(`Delete "${sec.title}"? Its pages will become unassigned.`)) return;
                updateDesign(d => ({
                  ...d,
                  sections: (d.sections||[]).filter(s => s.id !== sec.id),
                  pages:    (d.pages||[]).map(p => p.sectionId===sec.id ? {...p,sectionId:null} : p),
                }));
              }
              function moveSectionUp() {
                if (isFirst) return;
                updateDesign(d => {
                  const ss = [...(d.sections||[])];
                  [ss[si-1], ss[si]] = [ss[si], ss[si-1]];
                  return {...d, sections:ss};
                });
              }
              function moveSectionDown() {
                if (isLast) return;
                updateDesign(d => {
                  const ss = [...(d.sections||[])];
                  [ss[si], ss[si+1]] = [ss[si+1], ss[si]];
                  return {...d, sections:ss};
                });
              }
              function addSectionBefore() {
                const id = `sec_${Date.now()}`;
                updateDesign(d => {
                  const ss = [...(d.sections||[])];
                  ss.splice(si, 0, { id, title:`Section ${ss.length + 1}` });
                  return {...d, sections:ss};
                });
              }
              function addSectionAfter() {
                const id = `sec_${Date.now()}`;
                updateDesign(d => {
                  const ss = [...(d.sections||[])];
                  ss.splice(si + 1, 0, { id, title:`Section ${ss.length + 1}` });
                  return {...d, sections:ss};
                });
              }

              const AddSectionBtn = ({ label, onClick }) => (
                <button
                  onClick={onClick}
                  style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                    width:"100%", padding:"6px 0",
                    background:"none", border:"1.5px dashed #93C5FD", borderRadius:8,
                    fontSize:12, fontWeight:600, color:"#3B82F6", cursor:"pointer",
                    transition:"all 0.15s" }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor="#1D4ED8"; e.currentTarget.style.color="#1D4ED8"; e.currentTarget.style.background="#EFF6FF"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#93C5FD"; e.currentTarget.style.color="#3B82F6"; e.currentTarget.style.background="none"; }}
                >
                  + {label}
                </button>
              );

              return (
                <div key={sec.id} style={{ marginBottom:8 }}>
                  {/* Add Section BEFORE the first section */}
                  {isFirst && (
                    <div style={{ marginBottom:8 }}>
                      <AddSectionBtn label="Add Section before this" onClick={addSectionBefore} />
                    </div>
                  )}

                  {/* Section header — distinct deep blue, with SECTION label */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8,
                    padding:"8px 12px", background:SECTION_COLOR, borderRadius:8 }}>
                    <span style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em",
                      color:"rgba(255,255,255,0.55)", flexShrink:0 }}>SECTION</span>
                    <div style={{ width:20, height:20, borderRadius:4, background:"rgba(255,255,255,0.15)",
                      color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:700, flexShrink:0 }}>{si+1}</div>
                    <input
                      value={sec.title}
                      onChange={e => renameSection(e.target.value)}
                      placeholder="Section title…"
                      style={{ flex:1, fontSize:13, fontWeight:600, padding:"3px 8px",
                        background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)",
                        borderRadius:5, color:"#fff", outline:"none" }}
                    />
                    {/* Section colour picker */}
                    <input type="color" value={SECTION_COLOR}
                      onChange={e => recolorSection(e.target.value)}
                      title="Section colour"
                      onClick={e => e.stopPropagation()}
                      style={{ width:22, height:22, padding:2, borderRadius:4, flexShrink:0,
                        border:"1.5px solid rgba(255,255,255,0.3)", cursor:"pointer", background:"none" }}/>
                    <button className="btn-ghost" title="Move section up" disabled={isFirst}
                      style={{ padding:"2px 5px", fontSize:13, lineHeight:1,
                        color: isFirst ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)" }}
                      onClick={moveSectionUp}>▲</button>
                    <button className="btn-ghost" title="Move section down" disabled={isLast}
                      style={{ padding:"2px 5px", fontSize:13, lineHeight:1,
                        color: isLast ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)" }}
                      onClick={moveSectionDown}>▼</button>
                    {!isProtected && (
                      <button className="btn-ghost" title="Delete section" onClick={deleteSection}
                        style={{ padding:"2px 6px", fontSize:12, color:"rgba(255,150,150,0.85)", lineHeight:1 }}>✕</button>
                    )}
                  </div>

                  {/* Pages */}
                  <div style={{ paddingLeft:16, borderLeft:`3px solid ${SECTION_COLOR}` }}>
                    {secPages.length === 0 ? (
                      <div style={{ padding:"12px 14px", background:"var(--gray-50)",
                        border:"1px dashed var(--gray-300)", borderRadius:"var(--radius)",
                        fontSize:13, color:"var(--gray-400)", marginBottom:8 }}>
                        No pages yet — add one below.
                      </div>
                    ) : secPages.map(renderPageEditor)}
                    <button className="btn-secondary" style={{ width:"100%", fontSize:13, marginTop:4 }}
                      onClick={()=>updateDesign(d=>({...d, pages:[...(d.pages||[]),
                        { id:`page_${Date.now()}`, title:"", blocks:[], isFinalQuiz:false, sectionId:sec.id }] }))}>
                      + Add Page to "{sec.title}"
                    </button>
                  </div>

                  {/* Add Section after this section */}
                  <div style={{ marginTop:8 }}>
                    <AddSectionBtn
                      label={`Add Section ${isLast ? "" : `after "${sec.title}"`}`}
                      onClick={addSectionAfter}
                    />
                  </div>
                </div>
              );
            })}

            {/* Un-sectioned pages (shouldn't normally exist with forced sections) */}
            {unsectioned.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:8, paddingLeft:2 }}>
                  ⚠ Un-assigned pages — assign these to a section
                </div>
                <div style={{ paddingLeft:16, borderLeft:"3px solid var(--orange)" }}>
                  {unsectioned.map(renderPageEditor)}
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ── Video watch components ────────────────────────────────────────────────────

function loadYouTubeAPI() {
  if (window._ytApiLoading) return;
  window._ytApiLoading = true;
  if (!window.YT || !window.YT.Player) {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  }
}

function YouTubeWatchedPlayer({ videoId, caption, mustWatch, onWatched, watched }) {
  const containerRef = useRef(null);
  const playerRef    = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [started,  setStarted]  = useState(false);
  const [ready,    setReady]    = useState(false);

  useEffect(() => {
    loadYouTubeAPI();
    let cancelled = false;

    function fixIframe() {
      // Force iframe to fill the oversized container (which is larger than the clip box)
      const iframe = containerRef.current?.querySelector("iframe");
      if (iframe) {
        iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:none;";
      }
    }

    function create() {
      if (cancelled || !containerRef.current) return;
      containerRef.current.innerHTML = "";

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        width:  "100%",
        height: "100%",
        playerVars: {
          rel:            0,
          modestbranding: 1,
          controls:       0,
          disablekb:      1,
          fs:             0,
          iv_load_policy: 3,
          cc_load_policy: 0,
          playsinline:    1,
          origin:         window.location.origin,
        },
        events: {
          onReady: () => { setReady(true); fixIframe(); },
          onStateChange: (e) => {
            fixIframe(); // re-apply after YouTube resets styles
            const s = window.YT.PlayerState;
            if (e.data === s.PLAYING)  { setPlaying(true);  setStarted(true); }
            if (e.data === s.PAUSED || e.data === s.BUFFERING) setPlaying(false);
            if (e.data === s.ENDED)    { setPlaying(false); if (mustWatch) onWatched(); }
          },
        },
      });
      // Also fix after a short delay to catch late injection
      setTimeout(fixIframe, 500);
      setTimeout(fixIframe, 1500);
    }

    const timer = setInterval(() => {
      if (window.YT?.Player) { clearInterval(timer); create(); }
    }, 100);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); clearInterval(timer); create(); };

    return () => {
      cancelled = true;
      clearInterval(timer);
      try { playerRef.current?.destroy(); } catch {}
    };
  }, [videoId, mustWatch]); // eslint-disable-line

  function togglePlay() {
    if (!playerRef.current?.getPlayerState) return;
    try {
      if (playerRef.current.getPlayerState() === window.YT.PlayerState.PLAYING) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    } catch {}
  }

  // Clip the iframe to hide YouTube's title bar (~50px top) and bottom bar (~40px)
  // by making the iframe overflow the clipping box.
  const CROP_TOP    = 75;
  const CROP_BOTTOM = 75;

  return (
    <div className="native-block native-block--video">
      {/* Clipping box — hides YouTube UI chrome that overflows */}
      <div style={{ position:"relative", width:"100%", aspectRatio:"16/9", borderRadius:8, overflow:"hidden", background:"#000" }}>

        {/* Iframe is physically taller than clip box — overflows top and bottom,
            which gets clipped by overflow:hidden on the parent.
            This hides YouTube's title bar (top) and info/branding bar (bottom). */}
        <div ref={containerRef} style={{
          position:"absolute",
          top:`-${CROP_TOP}px`,
          left:0,
          width:"100%",
          height:`calc(100% + ${CROP_TOP + CROP_BOTTOM}px)`,
        }} />

        {/* Full-area interceptor */}
        <div onClick={togglePlay}
          style={{ position:"absolute", inset:0, zIndex:10, cursor:"pointer" }} />

        {/* Play button — before first interaction */}
        {!started && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:11, pointerEvents:"none" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg viewBox="0 0 24 24" fill="#fff" width="30" style={{ marginLeft:4 }}><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        )}

        {/* Pause icon */}
        {started && !playing && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:11, pointerEvents:"none" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg viewBox="0 0 24 24" fill="#fff" width="26"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            </div>
          </div>
        )}

        {/* Watch-to-unlock badge */}
        {mustWatch && !watched && (
          <div style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.8)", color:"#fff", fontSize:12, padding:"5px 14px", borderRadius:99, display:"flex", alignItems:"center", gap:6, zIndex:12, pointerEvents:"none", whiteSpace:"nowrap" }}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
            Watch in full to continue
          </div>
        )}
        {mustWatch && watched && (
          <div style={{ position:"absolute", bottom:10, right:10, background:"var(--green)", color:"#fff", fontSize:12, padding:"4px 10px", borderRadius:99, zIndex:12, pointerEvents:"none" }}>
            ✓ Watched
          </div>
        )}
      </div>
      {caption && <p style={{ fontSize:13, color:"var(--gray-500)", textAlign:"center", marginTop:8 }}>{caption}</p>}
    </div>
  );
}

// Vimeo player with ended detection via postMessage
function VimeoWatchedPlayer({ videoId, caption, mustWatch, onWatched, watched }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!mustWatch) return;

    function handleMessage(e) {
      if (!e.origin.includes("vimeo.com")) return;
      try {
        const data = JSON.parse(e.data);
        if (data.event === "finish") onWatched();
        // Register for finish events once the player is ready
        if (data.event === "ready") {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: "addEventListener", value: "finish" }),
            "https://player.vimeo.com"
          );
        }
      } catch {}
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [mustWatch, onWatched]);

  const src = `https://player.vimeo.com/video/${videoId}?api=1${mustWatch ? "&autopause=1" : ""}`;

  return (
    <div className="native-block native-block--video" style={{ position:"relative" }}>
      <iframe ref={iframeRef} src={src} title={caption||"Video"}
        allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
        style={{ width:"100%", aspectRatio:"16/9", border:"none", borderRadius:8 }}
      />
      {mustWatch && !watched && (
        <div style={{ position:"absolute", bottom:8, right:8, background:"rgba(0,0,0,0.7)", color:"#fff", fontSize:12, padding:"4px 10px", borderRadius:99, display:"flex", alignItems:"center", gap:5 }}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="12"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
          Watch to unlock
        </div>
      )}
      {mustWatch && watched && (
        <div style={{ position:"absolute", bottom:8, right:8, background:"var(--green)", color:"#fff", fontSize:12, padding:"4px 10px", borderRadius:99 }}>✓ Watched</div>
      )}
      {caption && <p style={{ fontSize:13, color:"var(--gray-500)", textAlign:"center", marginTop:6 }}>{caption}</p>}
    </div>
  );
}

// ── Drag & Drop Block ─────────────────────────────────────────────────────────
function DragDropBlock({ block, submitted, wasCorrect, userAnswer, onAnswer, onSubmit, isFinalQuizPage }) {
  const categories  = block.categories || [];
  const items       = block.items       || [];
  // placements: array of category index per item (same length as items)
  const placements  = Array.isArray(userAnswer) && userAnswer.length === items.length
    ? userAnswer
    : items.map(() => null);

  const [dragging, setDragging] = useState(null); // index of item being dragged

  function handleDrop(catIdx) {
    if (dragging === null || submitted) return;
    const next = [...placements];
    next[dragging] = catIdx;
    onAnswer(next);
    setDragging(null);
  }

  function handleRemove(itemIdx) {
    if (submitted) return;
    const next = [...placements];
    next[itemIdx] = null;
    onAnswer(next);
  }

  const allPlaced   = placements.every(p => p !== null);
  const unplaced    = items.filter((_, i) => placements[i] === null);

  return (
    <div className="native-block native-block--quiz">
      <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>⇄ {block.instruction || "Drag each item to the correct category"}</div>

      {/* Unplaced items bank */}
      {!submitted && unplaced.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-400)", marginBottom:8 }}>Items to sort</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {items.map((item, i) => placements[i] === null && (
              <div key={i} draggable
                onDragStart={() => setDragging(i)}
                onDragEnd={() => setDragging(null)}
                style={{ background:"#fff", border:"1.5px solid var(--red)", borderRadius:8, padding:"8px 14px", fontSize:14, cursor:"grab", userSelect:"none", boxShadow:"0 1px 3px rgba(0,0,0,0.1)", opacity: dragging===i ? 0.5 : 1 }}>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category drop zones */}
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${categories.length}, 1fr)`, gap:12, marginBottom:16 }}>
        {categories.map((cat, ci) => {
          const itemsHere = items.map((item,i) => ({ item, i })).filter(({ i }) => placements[i] === ci);
          const isDragOver = dragging !== null && placements[dragging] !== ci;
          return (
            <div key={ci}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={() => handleDrop(ci)}
              style={{ minHeight:100, background: isDragOver ? "var(--red-light)" : "var(--gray-50)", border:`2px dashed ${isDragOver ? "var(--red)" : "var(--gray-300)"}`, borderRadius:10, padding:12, transition:"all 0.15s" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--gray-600)", marginBottom:10, textAlign:"center" }}>{cat}</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {itemsHere.map(({ item, i }) => {
                  let bg="#fff", border="var(--gray-200)";
                  if (submitted) {
                    bg = item.category === ci ? "var(--green-light)" : "var(--red-light)";
                    border = item.category === ci ? "#10B981" : "var(--red)";
                  }
                  return (
                    <div key={i} draggable={!submitted}
                      onDragStart={() => !submitted && setDragging(i)}
                      onDragEnd={() => setDragging(null)}
                      style={{ background:bg, border:`1.5px solid ${border}`, borderRadius:8, padding:"7px 12px", fontSize:13, cursor:submitted?"default":"grab", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      {item.text}
                      {submitted && <span>{item.category===ci?"✓":"✗"}</span>}
                      {!submitted && <button type="button" style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"var(--gray-400)", padding:0, lineHeight:1 }} onClick={() => handleRemove(i)}>✕</button>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!submitted && !isFinalQuizPage ? (
        <button className="btn-primary" style={{ fontSize:13 }} disabled={!allPlaced}
          onClick={onSubmit}>
          {allPlaced ? "Submit" : `Place all items (${items.length - placements.filter(p=>p!==null).length} remaining)`}
        </button>
      ) : submitted ? (
        <div style={{ fontSize:13, padding:"10px 14px", background:wasCorrect?"var(--green-light)":"var(--red-light)", borderRadius:8, color:wasCorrect?"var(--green)":"var(--red)", fontWeight:500 }}>
          {wasCorrect?"✓ All correct!":"✗ Some items were in the wrong category"}{block.explanation?` — ${block.explanation}`:""}
        </div>
      ) : !allPlaced ? (
        <div style={{ fontSize:12, color:"var(--gray-400)" }}>{placements.filter(p=>p!==null).length}/{items.length} placed</div>
      ) : null}
    </div>
  );
}

// ── CoursePreview / Player ────────────────────────────────────────────────────
// ── AccordionBlock sub-component ─────────────────────────────────────────────
function AccordionBlock({ items, onOpen }) {
  const [open, setOpen] = useState(null);
  function handleOpen(i) { setOpen(open===i?null:i); if (onOpen) onOpen(i); }
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {items.map((item,i)=>(
        <div key={i} style={{ border:"1px solid var(--gray-200)", borderRadius:8, overflow:"hidden" }}>
          <button type="button" onClick={()=>handleOpen(i)}
            style={{ width:"100%", textAlign:"left", padding:"12px 16px", background:open===i?"var(--gray-50)":"#fff", border:"none", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14, fontWeight:600 }}>
            {item.title}
            <span style={{ fontSize:12, color:"var(--gray-400)", flexShrink:0 }}>{open===i?"▲":"▼"}</span>
          </button>
          {open===i && (
            <div style={{ padding:"12px 16px", borderTop:"1px solid var(--gray-200)", fontSize:14, lineHeight:1.7, color:"var(--gray-700)", whiteSpace:"pre-wrap" }}>
              {item.body}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── TabsBlock sub-component ───────────────────────────────────────────────────
function TabsBlock({ tabs, primary, onTabView }) {
  const [active, setActive] = useState(0);
  useEffect(() => { if (onTabView) onTabView(0); }, []); // eslint-disable-line
  function handleTab(i) { setActive(i); if (onTabView) onTabView(i); }
  return (
    <div>
      <div style={{ display:"flex", gap:0, borderBottom:"2px solid var(--gray-200)", overflowX:"auto" }}>
        {tabs.map((tab,i)=>(
          <button key={i} type="button" onClick={()=>handleTab(i)}
            style={{ padding:"10px 18px", fontSize:13, fontWeight:active===i?700:400, border:"none", borderBottom: active===i?`2px solid ${primary}`:"2px solid transparent", marginBottom:-2, background:"none", cursor:"pointer", color:active===i?primary:"var(--gray-600)", whiteSpace:"nowrap", transition:"all 0.15s" }}>
            {tab.label||`Tab ${i+1}`}
          </button>
        ))}
      </div>
      <div style={{ padding:"16px 4px", fontSize:14, lineHeight:1.7, color:"var(--gray-700)", whiteSpace:"pre-wrap" }}>
        {tabs[active]?.body}
      </div>
    </div>
  );
}

// ── ProcessStep sub-component ─────────────────────────────────────────────────
function ProcessStep({ step, index, total, onOpen }) {
  const [open, setOpen] = useState(false);
  function handleToggle() { const next=!open; setOpen(next); if (next && onOpen) onOpen(); }
  return (
    <div style={{ display:"flex", gap:0, position:"relative" }}>
      {/* Vertical connector */}
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginRight:14, flexShrink:0 }}>
        <div style={{ width:28, height:28, borderRadius:"50%", background:"var(--gray-800)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:13, zIndex:1 }}>{index+1}</div>
        {index < total-1 && <div style={{ width:2, flex:1, minHeight:16, background:"var(--gray-200)", marginTop:2 }}/>}
      </div>
      {/* Content */}
      <div style={{ flex:1, paddingBottom: index < total-1 ? 16 : 0 }}>
        <button type="button" style={{ width:"100%", textAlign:"left", background:"none", border:"none", cursor:"pointer", padding:0 }}
          onClick={handleToggle}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 0" }}>
            <span style={{ fontWeight:600, fontSize:14 }}>{step.title}</span>
            <span style={{ fontSize:12, color:"var(--gray-400)" }}>{open?"▲":"▼"}</span>
          </div>
        </button>
        {open && step.body && (
          <div style={{ fontSize:14, lineHeight:1.6, color:"var(--gray-600)", paddingTop:6, paddingBottom:4 }}>{step.body}</div>
        )}
      </div>
    </div>
  );
}

// ── FlashcardDeck sub-component ── stacked cards with flip animation ────────
function FlashcardDeck({ cards, onView, onLastCardFlipped }) {
  const [idx,       setIdx]      = useState(0);
  const [flipped,   setFlipped]  = useState(false);
  const [animating, setAnimating]= useState(false);
  const [leaving,   setLeaving]  = useState(null); // null | "prev" | "next"
  const card  = cards[idx] || {};
  const isLast = idx === cards.length - 1;
  const total  = cards.length;

  useEffect(() => { if (onView) onView(0); }, []); // eslint-disable-line

  function navigate(dir) {
    if (animating) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= total) return;
    setLeaving(dir > 0 ? "next" : "prev");
    setAnimating(true);
    setTimeout(() => {
      setIdx(newIdx);
      setFlipped(false);
      setLeaving(null);
      if (onView && newIdx < total - 1) onView(newIdx);
      setTimeout(() => setAnimating(false), 50);
    }, 280);
  }

  function handleFlip() {
    if (animating) return;
    const nowFlipped = !flipped;
    setFlipped(nowFlipped);
    if (nowFlipped && isLast && onLastCardFlipped) onLastCardFlipped();
  }

  const stackOffsets = [
    { x: 6,  y: -6,  scale: 0.96, opacity: 0.5, zIndex: 1 },
    { x: 3,  y: -3,  scale: 0.98, opacity: 0.7, zIndex: 2 },
    { x: 0,  y: 0,   scale: 1,    opacity: 1,   zIndex: 3 },
  ];
  const visibleStack = Math.min(3, total - idx);

  return (
    <div style={{ userSelect:"none" }}>
      <style>{`
        @keyframes fc-leave-next  { from{transform:translateX(0) scale(1);opacity:1} to{transform:translateX(-110%) scale(0.9);opacity:0} }
        @keyframes fc-leave-prev  { from{transform:translateX(0) scale(1);opacity:1} to{transform:translateX(110%) scale(0.9);opacity:0} }
        @keyframes fc-arrive      { from{transform:translateX(0) scale(0.92);opacity:0} to{transform:translateX(0) scale(1);opacity:1} }
        .fc-flip { transition: background 0.3s ease, color 0.3s ease; }
      `}</style>

      {/* Stack container */}
      <div style={{ position:"relative", height:200, marginBottom:16 }}>
        {/* Background stack cards */}
        {Array.from({ length: Math.min(visibleStack - 1, 2) }).map((_, si) => {
          const off = stackOffsets[si];
          return (
            <div key={si} style={{
              position:"absolute", inset:0,
              transform:`translate(${off.x}px, ${off.y}px) scale(${off.scale})`,
              opacity: off.opacity, zIndex: off.zIndex,
              background:"#fff", borderRadius:12,
              border:"2px solid var(--gray-200)",
              boxShadow:"0 2px 8px rgba(0,0,0,0.08)",
            }}/>
          );
        })}

        {/* Active card */}
        <div
          onClick={handleFlip}
          className="fc-flip"
          style={{
            position:"absolute", inset:0, zIndex:10,
            borderRadius:12, border:"2px solid var(--gray-200)",
            background: flipped ? "#1F2937" : "#fff",
            color: flipped ? "#fff" : "var(--gray-900)",
            cursor:"pointer", display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center",
            padding:"24px 28px", textAlign:"center",
            boxShadow:"0 4px 16px rgba(0,0,0,0.12)",
            animation: leaving ? `fc-leave-${leaving} 0.28s ease forwards`
                      : animating ? "fc-arrive 0.25s ease forwards"
                      : "none",
          }}
        >
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase",
            letterSpacing:"0.1em", opacity:0.45, marginBottom:10 }}>
            {flipped ? "Back" : "Front"} · {idx+1}/{total}
          </div>

          {/* Front content */}
          {!flipped && (
            card.frontImage
              ? <img src={card.frontImage} alt="card front"
                  style={{ maxHeight:120, maxWidth:"100%", objectFit:"contain", borderRadius:6, marginBottom: card.front ? 10 : 0 }}/>
              : null
          )}
          {!flipped && card.front && (
            <div style={{ fontSize:16, fontWeight:600, lineHeight:1.5 }}>{card.front}</div>
          )}

          {/* Back content */}
          {flipped && (
            card.backImage
              ? <img src={card.backImage} alt="card back"
                  style={{ maxHeight:120, maxWidth:"100%", objectFit:"contain", borderRadius:6, marginBottom: card.back ? 10 : 0 }}/>
              : null
          )}
          {flipped && card.back && (
            <div style={{ fontSize:15, lineHeight:1.6 }}>{card.back}</div>
          )}

          <div style={{ position:"absolute", bottom:10, right:14, fontSize:11, opacity:0.35 }}>
            {flipped ? "Click to flip back" : "Click to flip"}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <button className="btn-secondary" style={{ fontSize:13, padding:"6px 16px" }}
          disabled={idx === 0 || animating} onClick={() => navigate(-1)}>← Prev</button>
        <div style={{ display:"flex", gap:6 }}>
          {cards.map((_,i) => (
            <div key={i} style={{ width: i===idx ? 18 : 6, height:6, borderRadius:99,
              background: i===idx ? "#1F2937" : i<idx ? "#10B981" : "var(--gray-200)",
              transition:"all 0.2s" }}/>
          ))}
        </div>
        <button className="btn-secondary" style={{ fontSize:13, padding:"6px 16px" }}
          disabled={isLast || animating} onClick={() => navigate(1)}>Next →</button>
      </div>
    </div>
  );
}

// ── HotspotImage sub-component ────────────────────────────────────────────────
function HotspotImage({ src, hotspots, onOpen }) {
  const [active, setActive] = useState(null);
  function handleOpen(i) { setActive(active===i?null:i); if (onOpen) onOpen(i); }
  return (
    <div style={{ position:"relative", display:"inline-block", width:"100%" }}>
      <img src={src} alt="Hotspot" style={{ width:"100%", borderRadius:8, display:"block" }}/>
      {hotspots.map((h,i)=>(
        <div key={i}>
          {/* Pulse dot */}
          <button type="button" onClick={()=>handleOpen(i)}
            style={{ position:"absolute", left:`${h.x}%`, top:`${h.y}%`, transform:"translate(-50%,-50%)", width:28, height:28, borderRadius:"50%", background:"rgba(255,80,0,0.9)", border:"2px solid #fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:12, boxShadow:"0 2px 8px rgba(0,0,0,0.3)", zIndex:10 }}>
            {i+1}
          </button>
          {/* Popup */}
          {active===i && (
            <div style={{ position:"absolute", left:`${h.x}%`, top:`${h.y}%`, transform:"translate(-50%, 20px)", background:"#fff", border:"1px solid var(--gray-200)", borderRadius:8, padding:"12px 14px", minWidth:200, maxWidth:260, boxShadow:"0 8px 24px rgba(0,0,0,0.15)", zIndex:20 }}>
              {h.label && <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>{h.label}</div>}
              <div style={{ fontSize:13, lineHeight:1.6, color:"var(--gray-700)" }}>{h.body}</div>
              <button type="button" className="btn-ghost" style={{ fontSize:11, padding:"2px 6px", marginTop:8, color:"var(--gray-400)" }} onClick={()=>setActive(null)}>Close</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── MatchingPairs sub-component — drag & drop ────────────────────────────────
function MatchingPairs({ block, blockId, answers, setAnswers }) {
  const pairs    = block.pairs || [];
  const state    = answers[blockId] || {};
  const submitted= !!state.submitted;

  const [shuffled] = useState(() => [...pairs].map((_,i)=>i).sort(()=>Math.random()-0.5));
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const matchedRightFor = li => state[li] ?? null;
  const matchedLeftFor  = si => {
    const entry = Object.entries(state).find(([k,v])=>v===si && k!=="submitted" && !k.endsWith("_shuffled"));
    return entry ? parseInt(entry[0]) : null;
  };

  function handleDrop(si) {
    if (submitted || dragging == null) return;
    const prev = { ...state };
    delete prev[dragging];
    Object.keys(prev).forEach(k => { if (prev[k]===si && k!=="submitted") delete prev[k]; });
    prev[dragging] = si;
    setAnswers(a => ({ ...a, [blockId]: prev }));
    setDragging(null); setDragOver(null);
  }

  function handleSubmit() {
    setAnswers(a => ({
      ...a,
      [blockId]: { ...state, submitted:true },
      [`${blockId}_shuffled`]: shuffled,
    }));
  }

  function handleReset() {
    setAnswers(a => ({ ...a, [blockId]:{} }));
    setDragging(null);
  }

  const allMatched   = pairs.every((_,i) => matchedRightFor(i) != null);
  const totalCorrect = submitted ? pairs.filter((_,i) => shuffled[matchedRightFor(i)]===i).length : 0;
  const allCorrect   = submitted && totalCorrect === pairs.length;

  return (
    <div>
      {block.instruction && <div style={{ fontSize:14, color:"var(--gray-600)", marginBottom:6 }}>{block.instruction}</div>}
      {!submitted && <div style={{ fontSize:12, color:"var(--gray-400)", marginBottom:14 }}>Drag each item on the left onto its match on the right.</div>}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 32px 1fr", gap:"8px 0" }}>
        {/* Left column — draggable items */}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {pairs.map((p,li) => {
            const matchedSi = matchedRightFor(li);
            const isMatched = matchedSi != null;
            const isCorrect = submitted && isMatched && shuffled[matchedSi]===li;
            const isWrong   = submitted && isMatched && !isCorrect;
            const isDragging= dragging === li;
            const bg     = isDragging?"#EEF2FF":isCorrect?"#ECFDF5":isWrong?"#FEF2F2":isMatched?"#F0F9FF":"#fff";
            const border = isDragging?"#6366F1":isCorrect?"#10B981":isWrong?"#EF4444":isMatched?"#0EA5E9":"var(--gray-200)";
            return (
              <div key={li} draggable={!submitted}
                onDragStart={() => setDragging(li)}
                onDragEnd={() => { setDragging(null); setDragOver(null); }}
                style={{ padding:"10px 12px", borderRadius:8, fontSize:13, border:`2px solid ${border}`,
                  background:bg, cursor:submitted?"default":"grab", userSelect:"none",
                  opacity:isDragging?0.4:1, transition:"all 0.12s",
                  display:"flex", alignItems:"center", gap:8 }}>
                {!submitted && <span style={{ fontSize:13, opacity:0.3, flexShrink:0 }}>⠿</span>}
                <span style={{ flex:1 }}>{p.left}</span>
                {submitted && <span style={{ fontWeight:700, flexShrink:0, color:isCorrect?"#10B981":"#EF4444" }}>{isCorrect?"✓":"✗"}</span>}
              </div>
            );
          })}
        </div>

        {/* Centre connectors */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, alignItems:"center" }}>
          {pairs.map((_,li)=>{
            const matchedSi = matchedRightFor(li);
            const isMatched = matchedSi != null;
            const isCorrect = submitted && isMatched && shuffled[matchedSi]===li;
            const isWrong   = submitted && isMatched && !isCorrect;
            return (
              <div key={li} style={{ height:41, display:"flex", alignItems:"center" }}>
                <div style={{ width:24, height:2, borderRadius:1, transition:"all 0.12s",
                  background:isCorrect?"#10B981":isWrong?"#EF4444":isMatched?"#0EA5E9":"var(--gray-200)" }}/>
              </div>
            );
          })}
        </div>

        {/* Right column — fixed answer labels, act as drop targets */}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {shuffled.map((origIdx, si) => {
            const matchedLi     = matchedLeftFor(si);
            const isOver        = dragOver === si && !submitted;
            const isMatched     = matchedLi != null;
            const isCorrectSlot = submitted && isMatched && shuffled[si] === matchedLi;
            const isWrongSlot   = submitted && isMatched && !isCorrectSlot;
            const bg     = isOver?"#EEF2FF":isCorrectSlot?"#ECFDF5":isWrongSlot?"#FEF2F2":isMatched?"#F0F9FF":"#fff";
            const border = isOver?"2px solid #6366F1":isCorrectSlot?"2px solid #10B981":isWrongSlot?"2px solid #EF4444":isMatched?"2px solid #0EA5E9":"2px dashed #E5E7EB";
            return (
              <div key={si}
                onDragOver={e => { e.preventDefault(); setDragOver(si); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop(si)}
                style={{ padding:"10px 12px", borderRadius:8, fontSize:13,
                  border, background:bg, minHeight:41,
                  transition:"all 0.12s", display:"flex", alignItems:"center", gap:8 }}>
                {/* Always show the answer text */}
                <span style={{ flex:1, color:"var(--gray-800)" }}>{pairs[origIdx].right}</span>
                {/* Matched indicator — shows which left item was dropped here */}
                {isMatched && !submitted && (
                  <span style={{ fontSize:11, color:"#0EA5E9", flexShrink:0 }}>← matched</span>
                )}
                {submitted && isCorrectSlot && <span style={{ color:"#10B981", fontWeight:700, flexShrink:0 }}>✓</span>}
                {submitted && isWrongSlot   && <span style={{ color:"#EF4444", fontWeight:700, flexShrink:0 }}>✗</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop:14, display:"flex", gap:10, alignItems:"center" }}>
        {!submitted ? (
          <>
            <button className="btn-primary" style={{ fontSize:13 }} onClick={handleSubmit} disabled={!allMatched}>Submit Answers</button>
            {Object.keys(state).filter(k=>k!=="submitted").length > 0 && (
              <button className="btn-ghost" style={{ fontSize:12, color:"var(--gray-400)" }} onClick={handleReset}>Reset</button>
            )}
            {!allMatched && <span style={{ fontSize:12, color:"var(--gray-400)" }}>Match all pairs to submit</span>}
          </>
        ) : (
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            <div style={{ fontWeight:700, fontSize:14, color:allCorrect?"#10B981":"#EF4444" }}>
              {allCorrect ? "✓ All correct!" : `${totalCorrect}/${pairs.length} correct`}
            </div>
            {!allCorrect && <button className="btn-secondary" style={{ fontSize:13 }} onClick={handleReset}>Try again</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export function CoursePreview({ design, completionMode, onClose, preview, courseId, onComplete, userName, courseName: courseNameProp }) {
  const allPages  = design?.pages || [];
  const sections  = design?.sections || [];
  const theme     = design?.theme || {};
  const primary   = theme.primaryColor || "#1F2937";
  const passScore = design?.passScore ?? 80;
  const introEnabled = design?.introEnabled !== false;

  // Sort pages into correct play order: intro first, then sections in order, then unsectioned
  const sortedPages = (() => {
    const sectionOrder = [SEC0_ID, ...sections.map(s => s.id)];
    const withIdx = allPages.map(p => ({
      page: p,
      secOrder: sectionOrder.indexOf(p.sectionId ?? "__none__"),
    }));
    // Stable sort: pages with unknown sectionId go last
    withIdx.sort((a, b) => {
      const ai = a.secOrder === -1 ? 9999 : a.secOrder;
      const bi = b.secOrder === -1 ? 9999 : b.secOrder;
      return ai - bi;
    });
    return withIdx.map(x => x.page);
  })();

  // Filter out intro (Section 0) pages when intro is disabled
  const pages = introEnabled ? sortedPages : sortedPages.filter(p => p.sectionId !== SEC0_ID);

  const [pageIdx,  setPageIdx]  = useState(0);
  const [answers,  setAnswers]  = useState({});
  const [checked,  setChecked]  = useState({});
  const [watched,  setWatched]  = useState({});
  const [visited,  setVisited]  = useState({});
  const [done,     setDone]     = useState(false);
  const [score,    setScore]    = useState(null);
  const [quizBank, setQuizBank] = useState(null);
  const [sectionOverview, setSectionOverview] = useState(null);
  const [sectionProgress, setSectionProgress] = useState({});
  const [hasScrolled,   setHasScrolled]   = useState(false);
  const [needsScroll,   setNeedsScroll]   = useState(false);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [unlockedPages, setUnlockedPages] = useState(new Set([0])); // page indices accessible
  const scrollBodyRef = useRef(null);
  const hasVideo  = pages.some(p => (p.blocks||[]).some(b => b.type === "video"));
  const isMobile  = typeof window !== "undefined" && window.innerWidth < 768;
  // Sound check shows FIRST — before section overview
  // Also shows as the first screen even without video when on mobile (so we can warn)
  const [soundConfirmed, setSoundConfirmed] = useState(!hasVideo && !isMobile);

  // Reset scroll tracking whenever the page changes
  useEffect(() => {
    setHasScrolled(false);
    setNeedsScroll(false);
    // After a short delay (content renders), check if the body actually overflows
    const timer = setTimeout(() => {
      const el = scrollBodyRef.current;
      if (el && el.scrollHeight > el.clientHeight + 20) {
        setNeedsScroll(true);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [pageIdx]); // eslint-disable-line
  useEffect(() => {
    const finalPage = pages.find(p => p.isFinalQuiz);
    if (!finalPage) { setQuizBank(null); }
    else {
      const allQ   = (finalPage.blocks||[]).filter(isQuizBlock);
      const always = allQ.filter(b => b.alwaysShow);
      const pool   = allQ.filter(b => !b.alwaysShow && b.inBank);
      const count  = Math.min(design?.quizBankCount ?? 5, pool.length);
      const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, count);
      const selectedIds = new Set([...always.map(b=>b.id), ...shuffled.map(b=>b.id)]);
      setQuizBank(selectedIds);
    }
    // Load existing progress (resume support) — only when playing for real, not previewing
    if (!preview && courseId) {
      fetch(`/api/progress/${courseId}`)
        .then(r => r.json())
        .then(p => {
          const sp = p.sectionProgress || {};
          setSectionProgress(sp);
          // Find first incomplete numbered section to resume from
          if (Object.keys(sp).length > 0 && sections.length > 0) {
            const activeSections = sections.filter(s => pages.some(pg => pg.sectionId === s.id));
            const firstIncomplete = activeSections.find(s => sp[s.id] !== "complete");
            if (firstIncomplete) {
              // Resume to first page of that section
              const firstPageIdx = pages.findIndex(pg => pg.sectionId === firstIncomplete.id);
              if (firstPageIdx > 0) {
                // Will show section overview after sound confirmed
                return; // handled in soundConfirmed effect below
              }
            }
            // All sections complete — start from beginning (they're retaking)
          }
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  // Trigger section overview after sound confirmed — with resume logic
  useEffect(() => {
    if (!soundConfirmed) return;
    if (sectionOverview) return; // already showing an overview, don't reset it
    if (pageIdx > 0) return;    // learner is already into the course, don't interrupt

    if (!preview && courseId && Object.keys(sectionProgress).length > 0 && sections.length > 0) {
      // Resume: find first incomplete numbered section
      const activeSections = sections.filter(s => pages.some(pg => pg.sectionId === s.id));
      const firstIncomplete = activeSections.find(s => sectionProgress[s.id] !== "complete");
      if (firstIncomplete) {
        const targetIdx = pages.findIndex(pg => pg.sectionId === firstIncomplete.id);
        if (targetIdx >= 0) {
          setSectionOverview({ sectionId: firstIncomplete.id, targetIdx, isResume: true });
          return;
        }
      }
      // All sections complete — start from beginning (retake), no resume needed
      return;
    }

    // No saved progress — show overview for first numbered section if applicable
    const firstPage = pages[0];
    if (firstPage?.sectionId && firstPage.sectionId !== SEC0_ID && sections.length > 0) {
      setSectionOverview({ sectionId: firstPage.sectionId, targetIdx: 0 });
    }
  }, [soundConfirmed, sectionProgress]); // eslint-disable-line

  const page = pages[pageIdx];
  const isLast = pageIdx === pages.length - 1;

  // Get visible blocks for current page (apply quiz bank filter on final quiz pages)
  const visibleBlocks = (page?.blocks || []).filter(block => {
    if (!page.isFinalQuiz || !isQuizBlock(block) || !quizBank) return true;
    return quizBank.has(block.id);
  });

  // Check if all quiz blocks on current page have been submitted (not just answered)
  const pageQuizBlocks = visibleBlocks.filter(isQuizBlock);
  const isFinalQuizPage = !!page?.isFinalQuiz;
  const allSubmitted = isFinalQuizPage
    ? pageQuizBlocks.every(b => answers[b.id] !== undefined && (b.type !== "quiz_ms" || (Array.isArray(answers[b.id]) && answers[b.id].length === (b.requiredCount ?? (b.options||[]).filter(o=>o.correct).length))) && (b.type !== "quiz_dd" || (Array.isArray(answers[b.id]) && answers[b.id].every(p=>p!==null))))
    : pageQuizBlocks.every(b => checked[b.id]);

  // Mandatory correct: non-final-quiz MC/MS/TF/Matching that require correct answer before proceeding
  const mandatoryCorrectBlocks = isFinalQuizPage ? [] : visibleBlocks.filter(b =>
    b.mustBeCorrect && (b.type === "quiz_mc" || b.type === "quiz_ms" || b.type === "quiz_tf" || b.type === "matching")
  );
  const allMandatoryCorrect = mandatoryCorrectBlocks.every(b => {
    if (b.type === "matching") {
      const state    = answers[b.id] || {};
      if (!state.submitted) return false;
      const pairs    = b.pairs || [];
      const shuffled = answers[`${b.id}_shuffled`] || pairs.map((_,i)=>i);
      // Every left item (0..n-1) must have a slot matched where shuffled[slot] === leftIdx
      return pairs.every((_,li) => {
        const si = state[String(li)] ?? state[li]; // handle string/number key coercion
        if (si == null) return false;
        return shuffled[si] === li;
      });
    }
    if (!checked[b.id]) return false;
    return isAnswerCorrect(b, answers[b.id]);
  });

  // Mandatory interaction blocks: accordion, tabs, process, scenario, flashcard, hotspot
  const INTERACTION_TYPES = ["accordion","tabs","process","scenario","flashcard","hotspot"];
  const mandatoryInteractionBlocks = visibleBlocks.filter(b => b.mustInteract && INTERACTION_TYPES.includes(b.type));
  const allInteracted = mandatoryInteractionBlocks.every(b => {
    const v = visited[b.id] || new Set();
    if (b.type === "flashcard") {
      // Must have navigated all non-last cards AND flipped last card to see back
      const cards = b.cards || [];
      if (cards.length === 0) return true;
      if (cards.length === 1) return v.has("back");
      return [...Array(cards.length - 1).keys()].every(i => v.has(i)) && v.has("back");
    }
    const count = {
      accordion: (b.items||[]).length,
      tabs:      (b.tabs||[]).length,
      process:   (b.steps||[]).length,
      scenario:  1,
      hotspot:   (b.hotspots||[]).length,
    }[b.type] || 1;
    return v.size >= count;
  });

  const pageMustWatchVideos = visibleBlocks.filter(b => b.type === "video" && b.mustWatch);
  const allWatched = pageMustWatchVideos.every(b => watched[b.id]);
  const mustScroll    = needsScroll && !hasScrolled;
  const canAdvance = allSubmitted && allWatched && allMandatoryCorrect && allInteracted && !mustScroll;

  // Helper to mark an interaction item as visited
  function markVisited(blockId, itemIdx) {
    setVisited(v => {
      const cur = new Set(v[blockId] || []);
      cur.add(itemIdx);
      return { ...v, [blockId]: cur };
    });
  }

  function submitAll() {
    const updates = {};
    for (const b of pageQuizBlocks) updates[b.id] = true;
    setChecked(c => ({ ...c, ...updates }));
  }

  function getCorrectIdx(block) {
    if (block.type === "quiz_tf") return block.correct ?? 0;
    return (block.options||[]).findIndex(o => o.correct);
  }

  // For scoring: returns true if the user's answer is fully correct for any block type
  function isAnswerCorrect(block, userAnswer) {
    if (block.type === "quiz_tf") return userAnswer === (block.correct ?? 0);
    if (block.type === "quiz_mc") return userAnswer === (block.options||[]).findIndex(o => o.correct);
    if (block.type === "quiz_ms") {
      const correct = (block.options||[]).map((o,i) => o.correct ? i : -1).filter(i=>i>=0).sort().join(",");
      const given   = Array.isArray(userAnswer) ? [...userAnswer].sort().join(",") : "";
      return correct === given;
    }
    if (block.type === "quiz_dd") {
      return Array.isArray(userAnswer) && (block.items||[]).every((item,i) => userAnswer[i] === item.category);
    }
    return false;
  }

  function calcScore() {
    const finalPage = pages.find(p => p.isFinalQuiz);
    if (!finalPage) return 100;
    const quizBlocks = (finalPage.blocks||[]).filter(b => isQuizBlock(b) && (!quizBank || quizBank.has(b.id)));
    if (quizBlocks.length === 0) return 100;
    let earned=0, total=0;
    for (const b of quizBlocks) {
      const pts = b.points ?? 1;
      total += pts;
      if (isAnswerCorrect(b, answers[b.id])) earned += pts;
    }
    return total === 0 ? 100 : Math.round((earned/total)*100);
  }

  async function handleNext() {
    if (!isLast) {
      const nextIdx  = pageIdx + 1;
      const nextPage = pages[nextIdx];
      const currPage = pages[pageIdx];

      // Check if we're leaving a section — mark it complete if so
      const leavingSection = currPage?.sectionId &&
        currPage.sectionId !== SEC0_ID &&
        nextPage?.sectionId !== currPage.sectionId;

      if (leavingSection) {
        const updatedSP = { ...sectionProgress, [currPage.sectionId]: "complete" };
        setSectionProgress(updatedSP);
        // Persist section completion to server (fire and forget)
        if (!preview && courseId) {
          fetch(`/api/progress/${courseId}`, {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({ status:"in_progress", sectionProgress: updatedSP }),
          }).catch(() => {});
        }
      }

      // Show section overview when crossing into a new numbered section (not intro)
      if (
        sections.length > 0 &&
        nextPage?.sectionId &&
        nextPage.sectionId !== SEC0_ID &&
        nextPage.sectionId !== currPage?.sectionId
      ) {
        setSectionOverview({ sectionId: nextPage.sectionId, targetIdx: nextIdx });
        return;
      }
      setPageIdx(nextIdx);
      // Unlock the next page and the one after (so the prev button always works too)
      setUnlockedPages(prev => {
        const next = new Set(prev);
        next.add(nextIdx);
        if (nextIdx + 1 < pages.length) next.add(nextIdx + 1);
        return next;
      });
      return;
    }
    const s = calcScore();
    setScore(s);
    setDone(true);
    if (!preview && courseId) {
      const hasFinalQuiz = pages.some(p=>p.isFinalQuiz);
      const passed = !hasFinalQuiz || s >= passScore;
      const shouldComplete =
        completionMode === "last_page" ||
        (completionMode === "pass_quiz"     && passed) ||
        (completionMode === "last_and_pass" && passed);
      if (shouldComplete) {
        // Mark final section complete too
        const lastPage = pages[pages.length - 1];
        const finalSP = lastPage?.sectionId && lastPage.sectionId !== SEC0_ID
          ? { ...sectionProgress, [lastPage.sectionId]: "complete" }
          : sectionProgress;
        await fetch(`/api/progress/${courseId}`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ status:"completed", score:s, sectionProgress: finalSP }),
        });
        // Don't call onComplete here — let the user read the completion screen first.
        // onComplete is called when they click Close on the completion screen.
      }
    }
  }

  function renderBlock(block, isFinalQuizPage) {
    const submitted  = checked[block.id];
    const userAnswer = answers[block.id];
    const correctIdx = getCorrectIdx(block);
    const isCorrect  = userAnswer === correctIdx;

    if (block.type === "text") return (
      <div key={block.id} className="native-block native-block--text"
        dangerouslySetInnerHTML={{ __html: block.html||"" }} />
    );

    if (block.type === "image") return (
      <div key={block.id} className="native-block native-block--image">
        <img src={block.src} alt={block.alt||""} style={{ maxWidth:"100%", borderRadius:8 }} />
        {block.alt && <p style={{ fontSize:13, color:"var(--gray-500)", textAlign:"center", marginTop:6 }}>{block.alt}</p>}
      </div>
    );

    if (block.type === "video") {
      const src = block.src || "";
      const ytMatch = src.match(/(?:youtube\.com\/(?:watch\?(?:[^&]*&)*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      const vmMatch = src.match(/vimeo\.com\/(\d+)/);
      if (ytMatch) return (
        <YouTubeWatchedPlayer key={block.id} videoId={ytMatch[1]} caption={block.caption}
          mustWatch={!!block.mustWatch} watched={!!watched[block.id]}
          onWatched={() => setWatched(w => ({ ...w, [block.id]: true }))} />
      );
      if (vmMatch) return (
        <VimeoWatchedPlayer key={block.id} videoId={vmMatch[1]} caption={block.caption}
          mustWatch={!!block.mustWatch} watched={!!watched[block.id]}
          onWatched={() => setWatched(w => ({ ...w, [block.id]: true }))} />
      );
      return (
        <div key={block.id} className="native-block native-block--video" style={{ position:"relative" }}>
          <video src={src} controls onEnded={() => block.mustWatch && setWatched(w => ({ ...w, [block.id]: true }))}
            style={{ width:"100%", borderRadius:8 }} />
          {block.mustWatch && !watched[block.id] && (
            <div style={{ position:"absolute", bottom:8, right:8, background:"rgba(0,0,0,0.7)", color:"#fff", fontSize:12, padding:"4px 10px", borderRadius:99, display:"flex", alignItems:"center", gap:5 }}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="12"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
              Watch to unlock
            </div>
          )}
          {block.mustWatch && watched[block.id] && (
            <div style={{ position:"absolute", bottom:8, right:8, background:"var(--green)", color:"#fff", fontSize:12, padding:"4px 10px", borderRadius:99 }}>✓ Watched</div>
          )}
          {block.caption && <p style={{ fontSize:13, color:"var(--gray-500)", textAlign:"center", marginTop:6 }}>{block.caption}</p>}
        </div>
      );
    }

    if (block.type === "info") {
      const s = INFO_STYLES[block.style||"info"];
      return (
        <div key={block.id} className="native-block" style={{ background:s.bg, border:`1.5px solid ${s.border}`, borderRadius:8, padding:"14px 18px" }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>{s.icon} {block.heading}</div>
          <div style={{ fontSize:14, lineHeight:1.6 }}>{block.body}</div>
        </div>
      );
    }

    if (block.type === "keypoints") return (
      <div key={block.id} className="native-block" style={{ background:"var(--gray-50)", border:"1px solid var(--gray-200)", borderRadius:8, padding:"14px 18px" }}>
        {block.heading && <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>✦ {block.heading}</div>}
        <ul style={{ paddingLeft:20, margin:0 }}>
          {(block.points||[]).filter(Boolean).map((p,i) => <li key={i} style={{ fontSize:14, marginBottom:6, lineHeight:1.5 }}>{p}</li>)}
        </ul>
      </div>
    );

    // ── Quote ─────────────────────────────────────────────────────────────────
    if (block.type === "quote") return (
      <div key={block.id} className="native-block" style={{ borderLeft:"4px solid var(--gray-300)", paddingLeft:20, margin:"0 0 16px" }}>
        <p style={{ fontSize:16, fontStyle:"italic", color:"var(--gray-700)", lineHeight:1.7, margin:"0 0 10px" }}>"{block.text}"</p>
        {block.attribution && (
          <div style={{ fontSize:13, color:"var(--gray-500)" }}>
            <strong>{block.attribution}</strong>{block.role ? ` — ${block.role}` : ""}
          </div>
        )}
      </div>
    );

    // ── Table ─────────────────────────────────────────────────────────────────
    if (block.type === "table") {
      const headers = block.headers || [];
      const rows    = block.rows    || [];
      return (
        <div key={block.id} className="native-block">
          {block.heading && <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>{block.heading}</div>}
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:14 }}>
              <thead><tr>{headers.map((h,i)=>(
                <th key={i} style={{ padding:"8px 12px", background:"var(--gray-800)", color:"#fff", textAlign:"left", fontWeight:600, fontSize:13 }}>{h}</th>
              ))}</tr></thead>
              <tbody>{rows.map((row,ri)=>(
                <tr key={ri} style={{ background:ri%2===0?"#fff":"var(--gray-50)" }}>
                  {row.map((cell,ci)=>(
                    <td key={ci} style={{ padding:"8px 12px", borderBottom:"1px solid var(--gray-200)", fontSize:14 }}>{cell}</td>
                  ))}
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      );
    }

    // ── Process Steps ─────────────────────────────────────────────────────────
    if (block.type === "process") {
      const visCount = (visited[block.id]||new Set()).size;
      const total    = (block.steps||[]).length;
      return (
        <div key={block.id} className="native-block">
          {block.heading && <div style={{ fontWeight:700, fontSize:15, marginBottom:14 }}>{block.heading}</div>}
          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
            {(block.steps||[]).map((step,i)=>(
              <ProcessStep key={i} step={step} index={i} total={(block.steps||[]).length} onOpen={()=>markVisited(block.id,i)}/>
            ))}
          </div>
          {block.mustInteract && visCount<total && (
            <div style={{ fontSize:12, color:"#9A3412", marginTop:8, padding:"6px 10px", background:"#FFF7ED", borderRadius:6, border:"1px solid #FED7AA" }}>
              ⚠ Please expand all {total} steps to continue ({visCount}/{total} done)
            </div>
          )}
        </div>
      );
    }

    // ── Flowchart ─────────────────────────────────────────────────────────────
    if (block.type === "flowchart") {
      return (
        <div key={block.id} className="native-block">
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0 }}>
            {(block.steps||[]).map((s,i)=>(
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", width:"100%" }}>
                <div style={{ background:"#1F2937", color:"#fff", borderRadius:8, padding:"12px 20px", textAlign:"center", width:"100%", maxWidth:400 }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{s.label}</div>
                  {s.body && <div style={{ fontSize:13, opacity:0.8, marginTop:4, lineHeight:1.5 }}>{s.body}</div>}
                </div>
                {i < (block.steps||[]).length-1 && (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"4px 0" }}>
                    <div style={{ width:2, height:20, background:"var(--gray-300)" }}/>
                    <div style={{ width:0, height:0, borderLeft:"8px solid transparent", borderRight:"8px solid transparent", borderTop:"12px solid var(--gray-400)" }}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Accordion ─────────────────────────────────────────────────────────────
    if (block.type === "accordion") {
      const visCount = (visited[block.id]||new Set()).size;
      const total    = (block.items||[]).length;
      return (
        <div key={block.id} className="native-block">
          {block.heading && <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>{block.heading}</div>}
          <AccordionBlock items={block.items||[]} onOpen={i=>markVisited(block.id,i)}/>
          {block.mustInteract && visCount<total && (
            <div style={{ fontSize:12, color:"#9A3412", marginTop:8, padding:"6px 10px", background:"#FFF7ED", borderRadius:6, border:"1px solid #FED7AA" }}>
              ⚠ Please expand all {total} items to continue ({visCount}/{total} done)
            </div>
          )}
        </div>
      );
    }

    // ── Tabs ──────────────────────────────────────────────────────────────────
    if (block.type === "tabs") {
      const visCount = (visited[block.id]||new Set()).size;
      const total    = (block.tabs||[]).length;
      return (
        <div key={block.id} className="native-block">
          <TabsBlock tabs={block.tabs||[]} primary={primary} onTabView={i=>markVisited(block.id,i)}/>
          {block.mustInteract && visCount<total && (
            <div style={{ fontSize:12, color:"#9A3412", marginTop:8, padding:"6px 10px", background:"#FFF7ED", borderRadius:6, border:"1px solid #FED7AA" }}>
              ⚠ Please view all {total} tabs to continue ({visCount}/{total} viewed)
            </div>
          )}
        </div>
      );
    }

    // ── Columns ───────────────────────────────────────────────────────────────
    if (block.type === "columns") {
      const count = block.count || 2;
      const cols  = [block.col1||"", block.col2||"", ...(count===3?[block.col3||""]:[])] ;
      return (
        <div key={block.id} className="native-block">
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${count},1fr)`, gap:20 }}>
            {cols.map((c,i)=>(
              <div key={i} style={{ fontSize:14, lineHeight:1.7, color:"var(--gray-700)", whiteSpace:"pre-wrap" }}>{c}</div>
            ))}
          </div>
        </div>
      );
    }

    // ── Divider ───────────────────────────────────────────────────────────────
    if (block.type === "divider") {
      const s = block.style || "line";
      return (
        <div key={block.id} className="native-block" style={{ padding: s==="spaced" ? "24px 0" : "8px 0" }}>
          {block.label && <div style={{ textAlign:"center", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"var(--gray-400)", marginBottom:10 }}>{block.label}</div>}
          {s !== "spaced" && <hr style={{ border:"none", borderTop: s==="thick" ? "3px solid var(--gray-300)" : s==="dotted" ? "2px dotted var(--gray-300)" : "1px solid var(--gray-200)", margin:0 }}/>}
        </div>
      );
    }

    // ── Scenario / Branching ──────────────────────────────────────────────────
    if (block.type === "scenario") {
      const chosen   = answers[block.id];
      const choices  = block.choices || [];
      const selected = chosen != null ? choices[chosen] : null;
      const hasChosen = chosen != null;
      if (block.mustInteract && hasChosen) markVisited(block.id, 0);
      return (
        <div key={block.id} className="native-block native-block--quiz">
          <div style={{ fontSize:14, lineHeight:1.7, marginBottom:16, padding:"12px 16px", background:"var(--gray-50)", borderRadius:8, borderLeft:"4px solid var(--gray-400)" }}>
            {block.situation}
          </div>
          {chosen == null ? (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"var(--gray-500)", marginBottom:4 }}>What would you do?</div>
              {choices.map((c,i)=>(
                <button key={i} type="button"
                  style={{ background:"#fff", border:"1.5px solid var(--gray-200)", borderRadius:8, padding:"10px 14px", textAlign:"left", fontSize:14, cursor:"pointer" }}
                  onClick={()=>setAnswers(a=>({...a,[block.id]:i}))}>
                  <span style={{ fontWeight:600, marginRight:8 }}>{String.fromCharCode(65+i)}.</span>{c.label}
                </button>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ fontSize:13, color:"var(--gray-500)", marginBottom:6 }}>You chose: <strong>{selected?.label}</strong></div>
              <div style={{ padding:"12px 16px", borderRadius:8, fontSize:14, lineHeight:1.6,
                background: selected?.outcome==="good" ? "var(--green-light)" : selected?.outcome==="bad" ? "var(--red-light)" : "var(--gray-50)",
                border:`1.5px solid ${selected?.outcome==="good" ? "#10B981" : selected?.outcome==="bad" ? "var(--red)" : "var(--gray-200)"}`,
                color: selected?.outcome==="good" ? "var(--green)" : selected?.outcome==="bad" ? "var(--red)" : "var(--gray-700)",
              }}>
                {selected?.outcome==="good" && "✓ "}{selected?.outcome==="bad" && "✗ "}{selected?.feedback}
              </div>
              <button className="btn-secondary" style={{ fontSize:12, padding:"4px 10px", marginTop:10 }}
                onClick={()=>setAnswers(a=>({...a,[block.id]:undefined}))}>← Try another option</button>
            </div>
          )}
        </div>
      );
    }

    // ── Flashcards ────────────────────────────────────────────────────────────
    if (block.type === "flashcard") {
      const cards   = block.cards || [];
      const total   = cards.length;
      const vis     = visited[block.id] || new Set();
      // All non-last cards must be navigated to, AND last card back must be seen ("back" sentinel)
      const allCardsSeen  = total <= 1
        ? vis.has("back")
        : [...Array(total - 1).keys()].every(i => vis.has(i)) && vis.has("back");
      const visCount = vis.size; // for display
      return (
        <div key={block.id} className="native-block">
          {block.heading && <div style={{ fontWeight:700, fontSize:15, marginBottom:12 }}>🃏 {block.heading}</div>}
          <FlashcardDeck
            cards={cards}
            onView={i => {
              // Mark all non-last cards by index
              if (i < cards.length - 1) markVisited(block.id, i);
            }}
            onLastCardFlipped={() => markVisited(block.id, "back")}
          />
          {block.mustInteract && !allCardsSeen && (
            <div style={{ fontSize:12, color:"#9A3412", marginTop:8, padding:"6px 10px", background:"#FFF7ED", borderRadius:6, border:"1px solid #FED7AA" }}>
              ⚠ Please view all cards and flip the last card to see its answer to continue
            </div>
          )}
        </div>
      );
    }

    // ── Hotspot ───────────────────────────────────────────────────────────────
    if (block.type === "hotspot") {
      const visCount = (visited[block.id]||new Set()).size;
      const total    = (block.hotspots||[]).length;
      return (
        <div key={block.id} className="native-block">
          <HotspotImage src={block.src} hotspots={block.hotspots||[]} onOpen={i=>markVisited(block.id,i)}/>
          {block.mustInteract && visCount<total && (
            <div style={{ fontSize:12, color:"#9A3412", marginTop:8, padding:"6px 10px", background:"#FFF7ED", borderRadius:6, border:"1px solid #FED7AA" }}>
              ⚠ Please click all {total} hotspots to continue ({visCount}/{total} clicked)
            </div>
          )}
        </div>
      );
    }

    // ── Matching Pairs ────────────────────────────────────────────────────────
    if (block.type === "matching") {
      return (
        <div key={block.id} className="native-block">
          <MatchingPairs block={block} blockId={block.id} answers={answers} setAnswers={setAnswers}/>
        </div>
      );
    }

    if (isQuizBlock(block)) {
      const submitted  = checked[block.id];
      const userAnswer = answers[block.id];
      const wasCorrect = submitted ? isAnswerCorrect(block, userAnswer) : null;

      if (block.type === "quiz_mc" || block.type === "quiz_tf") {
        const isTF       = block.type === "quiz_tf";
        const options    = isTF ? ["True","False"] : (block.options||[]).map(o=>o.text);
        const correctIdx = getCorrectIdx(block);
        return (
          <div key={block.id} className="native-block native-block--quiz">
            <div style={{ fontWeight:600, fontSize:15, marginBottom:12 }}>❓ {block.question}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
              {options.map((opt,i) => {
                let bg="var(--gray-50)", border="var(--gray-200)", color="var(--gray-900)";
                if (submitted) {
                  if (i===correctIdx)                 { bg="var(--green-light)"; border="#10B981"; color="var(--green)"; }
                  else if (i===userAnswer&&!wasCorrect){ bg="var(--red-light)";   border="var(--red)"; color="var(--red)"; }
                } else if (userAnswer===i) { bg="var(--red-light)"; border="var(--red)"; }
                return (
                  <button key={i} type="button" disabled={submitted}
                    style={{ background:bg, border:`1.5px solid ${border}`, color, borderRadius:8, padding:"10px 14px", textAlign:"left", fontSize:14, cursor:submitted?"default":"pointer" }}
                    onClick={() => setAnswers(a=>({...a,[block.id]:i}))}>
                    <span style={{ fontWeight:600, marginRight:8 }}>{String.fromCharCode(65+i)}.</span>
                    {opt}
                    {submitted && i===correctIdx && " ✓"}
                    {submitted && i===userAnswer && !wasCorrect && " ✗"}
                  </button>
                );
              })}
            </div>
            {!submitted && !isFinalQuizPage ? (
              <button className="btn-primary" style={{ fontSize:13 }} disabled={userAnswer===undefined}
                onClick={() => setChecked(c=>({...c,[block.id]:true}))}>Submit Answer</button>
            ) : submitted ? (
              <div style={{ fontSize:13, padding:"10px 14px", background:wasCorrect?"var(--green-light)":"var(--red-light)", borderRadius:8, color:wasCorrect?"var(--green)":"var(--red)", fontWeight:500 }}>
                {wasCorrect?"✓ Correct!":"✗ Incorrect"}{block.explanation?` — ${block.explanation}`:""}
              </div>
            ) : null}
          </div>
        );
      }

      if (block.type === "quiz_ms") {
        const options       = block.options||[];
        const requiredCount = block.requiredCount ?? options.filter(o=>o.correct).length;
        const selected      = Array.isArray(userAnswer) ? userAnswer : [];
        const correctIdxs   = options.map((o,i)=>o.correct?i:-1).filter(i=>i>=0);
        return (
          <div key={block.id} className="native-block native-block--quiz">
            <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>❓ {block.question}</div>
            <div style={{ fontSize:13, color:"var(--gray-500)", marginBottom:12 }}>Select {requiredCount} answer{requiredCount!==1?"s":""}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
              {options.map((opt,i) => {
                const isSel = selected.includes(i);
                let bg="var(--gray-50)", border="var(--gray-200)", color="var(--gray-900)";
                if (submitted) {
                  if (correctIdxs.includes(i))       { bg="var(--green-light)"; border="#10B981"; color="var(--green)"; }
                  else if (isSel)                    { bg="var(--red-light)"; border="var(--red)"; color="var(--red)"; }
                } else if (isSel) { bg="var(--red-light)"; border="var(--red)"; }
                return (
                  <button key={i} type="button" disabled={submitted}
                    style={{ background:bg, border:`1.5px solid ${border}`, color, borderRadius:8, padding:"10px 14px", textAlign:"left", fontSize:14, cursor:submitted?"default":"pointer", display:"flex", alignItems:"center", gap:10 }}
                    onClick={() => { if(submitted) return; setAnswers(a=>({...a,[block.id]: isSel?selected.filter(x=>x!==i):[...selected,i]})); }}>
                    <span style={{ width:18, height:18, border:`2px solid ${border}`, borderRadius:4, background:isSel&&!submitted?"var(--red)":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {isSel && <svg viewBox="0 0 12 12" fill="none" width="10"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                    </span>
                    {opt.text}
                    {submitted && correctIdxs.includes(i) && " ✓"}
                    {submitted && isSel && !correctIdxs.includes(i) && " ✗"}
                  </button>
                );
              })}
            </div>
            {!submitted && !isFinalQuizPage ? (
              <button className="btn-primary" style={{ fontSize:13 }} disabled={selected.length !== requiredCount}
                onClick={() => setChecked(c=>({...c,[block.id]:true}))}>
                Submit ({selected.length}/{requiredCount})
              </button>
            ) : submitted ? (
              <div style={{ fontSize:13, padding:"10px 14px", background:wasCorrect?"var(--green-light)":"var(--red-light)", borderRadius:8, color:wasCorrect?"var(--green)":"var(--red)", fontWeight:500 }}>
                {wasCorrect?"✓ All correct!":"✗ Incorrect"}{block.explanation?` — ${block.explanation}`:""}
              </div>
            ) : !submitted && isFinalQuizPage && selected.length > 0 ? (
              <div style={{ fontSize:12, color:"var(--gray-400)" }}>{selected.length}/{requiredCount} selected</div>
            ) : null}
          </div>
        );
      }

      if (block.type === "quiz_dd") {
        return (
          <DragDropBlock key={block.id} block={block} submitted={submitted} wasCorrect={wasCorrect}
            userAnswer={userAnswer} isFinalQuizPage={isFinalQuizPage}
            onAnswer={ans => setAnswers(a=>({...a,[block.id]:ans}))}
            onSubmit={() => setChecked(c=>({...c,[block.id]:true}))} />
        );
      }
    }
    return null;
  }

  // ── 1. Sound / mobile intro — always first if course has video or device is mobile ──
  if (!soundConfirmed) {
    return (
      <div className="pdf-overlay">
        <div className="pdf-overlay-header" style={{ background: primary, color:"#fff" }}>
          <div className="pdf-overlay-title" style={{ color:"#fff" }}>{theme.title || "Training Course"}</div>
          {onClose && <button className="btn-ghost" style={{ color:"#fff" }} onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>}
        </div>
        <div className="pdf-overlay-body" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ padding:"40px 24px", maxWidth:480, width:"100%" }}>

            {/* Mobile warning */}
            {isMobile && (
              <div style={{ background:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:10, padding:"16px 20px", marginBottom:24, textAlign:"left" }}>
                <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                  <span style={{ fontSize:28, flexShrink:0 }}>💻</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, color:"#92400E", marginBottom:6 }}>
                      Best experienced on a computer
                    </div>
                    <p style={{ fontSize:13, color:"#78350F", lineHeight:1.6, margin:0 }}>
                      This course is designed to be completed on a desktop or laptop. You may continue on your mobile, but some interactive elements may not work correctly and you may be unable to complete the course.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Audio warning */}
            {hasVideo && (
              <div style={{ textAlign:"center", marginBottom: isMobile ? 16 : 0 }}>
                <div style={{ fontSize: isMobile ? 48 : 64, marginBottom:16 }}>🔊</div>
                <h2 style={{ fontSize:22, fontWeight:700, marginBottom:12, color:"var(--gray-900)" }}>
                  This course contains video
                </h2>
                <p style={{ fontSize:15, color:"var(--gray-600)", lineHeight:1.7, marginBottom:28 }}>
                  You will need sound for this training course. Please ensure you have <strong>headphones or a speaker</strong> before proceeding.
                </p>
              </div>
            )}

            {/* If only mobile warning (no video), add a small spacer heading */}
            {!hasVideo && isMobile && (
              <div style={{ textAlign:"center", marginBottom:8 }}>
                <h2 style={{ fontSize:20, fontWeight:700, color:"var(--gray-900)", marginBottom:4 }}>Ready to begin?</h2>
              </div>
            )}

            <button className="btn-primary" style={{ background: primary, fontSize:15, padding:"12px 32px", width:"100%" }}
              onClick={() => setSoundConfirmed(true)}>
              {isMobile ? "Continue on mobile →" : "I'm ready — Start Course →"}
            </button>

          </div>
        </div>
      </div>
    );
  }

  // ── 2. Section overview ───────────────────────────────────────────────────────
  if (sectionOverview && sections.length > 0) {
    const { sectionId, targetIdx, isResume } = sectionOverview;
    const sec = sections.find(s => s.id === sectionId);

    // Only show sections that have at least one page (exclude intro SEC0)
    const activeSections = sections.filter(s => pages.some(p => p.sectionId === s.id));
    const activeSecIdx   = activeSections.findIndex(s => s.id === sectionId);

    // Progress per section: use persisted sectionProgress for completed state
    const sectionProgressMap = activeSections.map(s => {
      const isCompleted = sectionProgress[s.id] === "complete";
      const sPages      = pages.filter(p => p.sectionId === s.id);
      const nVisited    = isCompleted ? sPages.length : sPages.filter(p => pages.indexOf(p) < targetIdx).length;
      const pct         = sPages.length ? Math.round((nVisited / sPages.length) * 100) : 0;
      return { section:s, total:sPages.length, visited:nVisited, pct, isCompleted };
    });

    const isFirst = !isResume && (targetIdx === 0 || (targetIdx > 0 && pages[targetIdx - 1]?.sectionId === SEC0_ID));

    return (
      <div className="pdf-overlay">
        <div className="pdf-overlay-header" style={{ background:primary, color:"#fff" }}>
          <div className="pdf-overlay-title" style={{ color:"#fff" }}>{theme.title||"Training Course"}</div>
          {onClose && <button className="btn-ghost" style={{ color:"#fff" }} onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>}
        </div>
        <div className="pdf-overlay-body" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ maxWidth:520, width:"100%", padding:"40px 24px" }}>
            <div style={{ textAlign:"center", marginBottom:28 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>{isResume ? "👋" : "📋"}</div>
              <h2 style={{ fontSize:22, fontWeight:700, marginBottom:4 }}>
                {isResume ? "Welcome back!" : isFirst ? `Welcome to ${sec?.title}` : `Up Next: ${sec?.title}`}
              </h2>
              <p style={{ fontSize:14, color:"var(--gray-500)" }}>
                {isResume
                  ? `You left off at ${sec?.title} — starting that section from the beginning`
                  : `Section ${activeSecIdx + 1} of ${activeSections.length}`}
              </p>
            </div>

            {/* All active sections progress list */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {sectionProgressMap.map((sp, i) => {
                const isCurrent  = sp.section.id === sectionId;
                const isComplete = sp.isCompleted;
                const isFuture   = !isCurrent && !isComplete && i > activeSecIdx;
                return (
                  <div key={sp.section.id} style={{ padding:"14px 16px", borderRadius:10, border:`2px solid ${isCurrent?primary:isComplete?"#10B981":"var(--gray-200)"}`, background:isCurrent?"#F0F4F8":isComplete?"#ECFDF5":"var(--gray-50)", opacity:isFuture?0.6:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:isComplete?"#10B981":isCurrent?primary:"var(--gray-300)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, flexShrink:0 }}>
                        {isComplete ? "✓" : i + 1}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:isCurrent?700:500, fontSize:14, color:isCurrent?primary:"var(--gray-700)" }}>{sp.section.title}</div>
                        <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:1 }}>{sp.total} page{sp.total!==1?"s":""}</div>
                      </div>
                      <div style={{ fontSize:12, fontWeight:600, color:isComplete?"#10B981":isCurrent?primary:"var(--gray-400)" }}>
                        {isCurrent ? (isResume ? "Resuming" : "Up next") : isComplete ? "✓ Complete" : isFuture ? "" : `${sp.pct}%`}
                      </div>
                    </div>
                    {isComplete && sp.total > 0 && (
                      <div style={{ marginTop:8 }}>
                        <div style={{ height:5, background:"var(--gray-200)", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", background:"#10B981", width:"100%", transition:"width 0.4s" }}/>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button className="btn-primary" style={{ width:"100%", marginTop:24, padding:"12px", fontSize:15, background:primary }}
              onClick={() => { setSectionOverview(null); setPageIdx(targetIdx); setUnlockedPages(prev => { const n=new Set(prev); n.add(targetIdx); if(targetIdx+1<pages.length) n.add(targetIdx+1); return n; }); }}>
              {isResume ? `Resume — Start ${sec?.title} →` : isFirst ? `Start ${sec?.title} →` : `Continue to ${sec?.title} →`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 3. Completion screen ──────────────────────────────────────────────────────
  if (done) {
    const hasFinalQuiz = pages.some(p=>p.isFinalQuiz);
    const passed = !hasFinalQuiz || score >= passScore;
    const enableCertificate = design?.enableCertificate && passed && !preview;

    // When the user dismisses the completion screen, notify the parent so it can
    // refresh the course list. onComplete triggers the reload; onClose just shuts the modal.
    function handleCompletionClose() {
      if (onComplete && passed) onComplete(score);
      else if (onClose) onClose();
    }

    function downloadCertificate() {
      const url = `/api/courses/${courseId}/certificate`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${(courseNameProp||"course").replace(/[^a-z0-9]/gi,"-").toLowerCase()}.pdf`;
      a.click();
    }

    return (
      <div className="pdf-overlay">
        <div className="pdf-overlay-header">
          <div className="pdf-overlay-title">{theme.title||"Course Complete"}</div>
          <button className="btn-ghost" onClick={handleCompletionClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>
        <div className="pdf-overlay-body" style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ textAlign:"center", padding:40, maxWidth:480 }}>
            <div style={{ fontSize:64, marginBottom:16 }}>{passed?"🎉":"📚"}</div>
            <h2 style={{ fontSize:24, fontWeight:700, marginBottom:8 }}>{passed?"Course Complete!":"Almost There"}</h2>
            {hasFinalQuiz && <div style={{ fontSize:48, fontWeight:800, color:passed?"var(--green)":"var(--red)", margin:"16px 0" }}>{score}%</div>}
            {hasFinalQuiz && <div style={{ fontSize:13, color:"var(--gray-500)", marginBottom:8 }}>Pass score: {passScore}%</div>}
            <p style={{ fontSize:14, color:"var(--gray-500)", lineHeight:1.6, marginBottom:24 }}>
              {passed ? "Well done — you've successfully completed this training." : `You need ${passScore}% or more to pass. Please review the material and try again.`}
            </p>

            {/* Certificate download */}
            {enableCertificate && (
              <div style={{ background:"linear-gradient(135deg,#FEF9E7,#FDF3C0)", border:"1.5px solid #F59E0B", borderRadius:12, padding:"20px 24px", marginBottom:20 }}>
                <div style={{ fontSize:28, marginBottom:8 }}>🏅</div>
                <div style={{ fontWeight:700, fontSize:15, color:"#78350F", marginBottom:4 }}>Your certificate is ready</div>
                <div style={{ fontSize:13, color:"#92400E", marginBottom:14 }}>Download your Certificate of Completion for your records.</div>
                <button className="btn-primary" style={{ background:"#B45309", fontSize:14, padding:"10px 24px" }} onClick={downloadCertificate}>
                  ⬇ Download Certificate (PDF)
                </button>
              </div>
            )}

            <div style={{ display:"flex", justifyContent:"center", gap:10 }}>
              <button className="btn-primary" onClick={handleCompletionClose}>Close</button>
              {!passed && <button className="btn-secondary" onClick={() => { setPageIdx(0); setAnswers({}); setChecked({}); setWatched({}); setDone(false); setScore(null); setQuizBank(null); if (hasVideo || isMobile) setSoundConfirmed(false); }}>Retry</button>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Section colour for current page — changes as learner moves between sections
  const currentSec   = sections.find(s => s.id === page?.sectionId);
  const headerColor  = (page?.sectionId && page.sectionId !== SEC0_ID && currentSec?.color)
    ? currentSec.color : primary;

  return (
    <div className="pdf-overlay">
      <div className="pdf-overlay-header" style={{ background:headerColor, color:"#fff", transition:"background 0.4s" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {sections.length > 0 && (
            <button className="btn-ghost" style={{ color:"#fff", padding:"4px 8px", fontSize:13,
              background: sidebarOpen ? "rgba(255,255,255,0.2)" : "transparent",
              borderRadius:4, border:"1px solid rgba(255,255,255,0.2)" }}
              title="Course contents"
              onClick={() => setSidebarOpen(o=>!o)}>
              ☰ Contents
            </button>
          )}
          <div className="pdf-overlay-title" style={{ color:"#fff" }}>
            {theme.title||"Course"}{page?.title ? ` — ${page.title}` : ""}
            {page?.isFinalQuiz && <span style={{ marginLeft:8, fontSize:12, opacity:0.85 }}>🏆 Final Quiz</span>}
            {currentSec && page?.sectionId !== SEC0_ID && (
              <span style={{ marginLeft:8, fontSize:12, opacity:0.75 }}>· {currentSec.title}</span>
            )}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <span style={{ fontSize:12, opacity:0.8 }}>{pageIdx+1} / {pages.length}</span>
          {onClose && <button className="btn-ghost" style={{ color:"#fff" }} onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>}
        </div>
      </div>

      {/* Progress bar — uses section colour */}
      <div style={{ height:4, background:"rgba(0,0,0,0.1)" }}>
        <div style={{ height:"100%", background:headerColor, opacity:0.7, width:`${((pageIdx+1)/pages.length)*100}%`, transition:"width 0.3s, background 0.4s" }}/>
      </div>

      {/* Main content area: sidebar + page body */}
      <div style={{ display:"flex", flex:1, overflow:"hidden", position:"relative" }}>

        {/* Section navigation sidebar */}
        {sidebarOpen && (
          <div style={{ width:240, flexShrink:0, borderRight:"1px solid var(--gray-200)",
            overflowY:"auto", background:"#F9FAFB", display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"12px 14px", borderBottom:"1px solid var(--gray-200)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)" }}>Contents</span>
              <button className="btn-ghost" style={{ fontSize:12, color:"var(--gray-400)", padding:"2px 6px" }} onClick={()=>setSidebarOpen(false)}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:"auto", padding:"8px 0" }}>
              {/* Intro section */}
              {introEnabled && (() => {
                const introPageList = pages.filter(p => p.sectionId === SEC0_ID);
                if (!introPageList.length) return null;
                return (
                  <div key="intro" style={{ marginBottom:4 }}>
                    <div style={{ padding:"6px 14px 4px", fontSize:11, fontWeight:700,
                      textTransform:"uppercase", letterSpacing:"0.07em", color:"#4B5563", background:"#F0F1F3" }}>
                      Introduction
                    </div>
                    {introPageList.map(p => {
                      const pi = pages.indexOf(p);
                      const isUnlocked = unlockedPages.has(pi);
                      const isCurrent  = pi === pageIdx;
                      return (
                        <button key={p.id} disabled={!isUnlocked}
                          onClick={() => { setPageIdx(pi); setSidebarOpen(false); setHasScrolled(false); }}
                          style={{ display:"flex", alignItems:"center", gap:8, width:"100%", textAlign:"left",
                            padding:"7px 14px 7px 20px", fontSize:12, border:"none", cursor:isUnlocked?"pointer":"default",
                            background: isCurrent ? `${primary}18` : "transparent",
                            color: isCurrent ? primary : isUnlocked ? "var(--gray-700)" : "var(--gray-400)",
                            fontWeight: isCurrent ? 700 : 400, transition:"background 0.12s" }}>
                          <span style={{ fontSize:10, flexShrink:0 }}>{isUnlocked ? (isCurrent?"▶":"○") : "🔒"}</span>
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.title||"Page"}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Numbered sections */}
              {sections.map((sec, si) => {
                const secPages = pages.filter(p => p.sectionId === sec.id);
                if (!secPages.length) return null;
                const secColor = sec.color || "#0F4C81";
                const firstIdx = pages.indexOf(secPages[0]);
                const secUnlocked = unlockedPages.has(firstIdx);
                const isCurSec = secPages.some(p => pages.indexOf(p) === pageIdx);
                return (
                  <div key={sec.id} style={{ marginBottom:4 }}>
                    <div style={{ padding:"6px 14px 4px", fontSize:11, fontWeight:700,
                      textTransform:"uppercase", letterSpacing:"0.07em",
                      color: secUnlocked ? "#fff" : "var(--gray-400)",
                      background: secUnlocked ? secColor : "var(--gray-200)",
                      display:"flex", alignItems:"center", gap:6, transition:"all 0.15s" }}>
                      <span>{secUnlocked ? `${si+1}.` : "🔒"}</span>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sec.title}</span>
                    </div>
                    {secUnlocked && secPages.map(p => {
                      const pi = pages.indexOf(p);
                      const isUnlocked = unlockedPages.has(pi);
                      const isCurrent  = pi === pageIdx;
                      return (
                        <button key={p.id} disabled={!isUnlocked}
                          onClick={() => { setPageIdx(pi); setSidebarOpen(false); setHasScrolled(false); setNeedsScroll(false); }}
                          style={{ display:"flex", alignItems:"center", gap:8, width:"100%", textAlign:"left",
                            padding:"7px 14px 7px 20px", fontSize:12, border:"none", cursor:isUnlocked?"pointer":"default",
                            background: isCurrent ? `${secColor}20` : "transparent",
                            color: isCurrent ? secColor : isUnlocked ? "var(--gray-700)" : "var(--gray-400)",
                            fontWeight: isCurrent ? 700 : 400, borderLeft: isCurrent ? `3px solid ${secColor}` : "3px solid transparent",
                            transition:"all 0.12s" }}>
                          <span style={{ fontSize:10, flexShrink:0 }}>{isUnlocked ? (isCurrent?"▶":"○") : "🔒"}</span>
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.title||"Page"}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

      <div className="pdf-overlay-body" ref={scrollBodyRef}
        style={{ overflowY:"auto", padding:"24px 0" }}
        onScroll={e => {
          if (!hasScrolled) {
            const el = e.currentTarget;
            // Consider "scrolled" when within 40px of the bottom
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
              setHasScrolled(true);
            }
          }
        }}>
        <div style={{ maxWidth:720, margin:"0 auto", padding:"0 24px" }}>
          {page?.title && <h1 style={{ fontSize:22, fontWeight:700, marginBottom:20, color:"#111827" }}>{page.title}</h1>}
          {visibleBlocks.map(block => renderBlock(block, isFinalQuizPage))}

          {/* Blocking requirements warnings */}
          {mustScroll && (
            <div style={{ marginTop:16, padding:"10px 14px", background:"#EFF6FF", border:"1px solid #93C5FD", borderRadius:8, fontSize:13, color:"#1E40AF" }}>
              ↓ Please read the whole page before continuing.
            </div>
          )}
          {!canAdvance && allSubmitted && pageMustWatchVideos.filter(b=>!watched[b.id]).length > 0 && (
            <div style={{ marginTop:16, padding:"10px 14px", background:"var(--orange-light)", border:"1px solid var(--orange)", borderRadius:8, fontSize:13, color:"#7A3C00" }}>
              ⚠ Please watch {pageMustWatchVideos.filter(b => !watched[b.id]).length === 1 ? "the video" : "all videos"} before continuing.
            </div>
          )}
          {!canAdvance && !allMandatoryCorrect && (
            <div style={{ marginTop:16, padding:"10px 14px", background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:8, fontSize:13, color:"#9A3412" }}>
              ⚠ Please answer all required questions correctly before continuing.
            </div>
          )}
          {!canAdvance && !allInteracted && (
            <div style={{ marginTop:16, padding:"10px 14px", background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:8, fontSize:13, color:"#9A3412" }}>
              ⚠ Please interact with all required elements before continuing.
            </div>
          )}
        </div>
      </div>

      <div style={{ padding:"14px 24px", borderTop:"1px solid var(--gray-200)", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#fff" }}>
        <button className="btn-secondary" disabled={pageIdx===0} onClick={() => setPageIdx(i=>i-1)}>← Back</button>
        <button className="btn-primary" style={{ background:primary }}
          disabled={!canAdvance}
          onClick={handleNext}>
          {isLast ? "Finish" : "Next →"}
        </button>
      </div>
        </div> {/* end page content inner */}
      </div>   {/* end flex row (sidebar + content) */}
    </div>
  );
}
